import React, { useState, useCallback, useEffect } from 'react';
import { useData, DOCTOR_INFO } from '../context/DataContext';
import useApiPatients from '../hooks/useApiPatients';
import useApiAdmittedPatients from '../hooks/useApiAdmittedPatients';
import { createPatient, createBabyRecord, createDischargeRequest, createMedicalRecord, updateMedicalRecord, getMedicalRecords, getPatientDropdown } from '../services/api';

import useApiBabies from '../hooks/useApiBabies';
const DoctorPanel = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('patients');
  const { patients, birthRecords, medicalRecords, labReports, getStats } = useData();
  const stats = getStats();

  const tabs = [
    { id: 'patients', label: 'Patient Management', icon: '🏥' },
    { id: 'medical', label: 'Medical Records', icon: '📋' },
    { id: 'babies', label: 'Baby Records', icon: '👶' },
    { id: 'labreports', label: 'Lab Reports', icon: '🔬' },
    { id: 'outdoor', label: 'Outdoor Patients', icon: '🩺' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="Logo" className="w-10 h-10" />
            <div>
              <h1 className="font-bold text-gray-900">Doctor / Nurse Panel</h1>
              <p className="text-xs text-gray-500">Patient Care & Medical Records</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">👩‍⚕️ {user.name}</span>
            <button onClick={onLogout} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="stat-card"><p className="text-gray-500 text-sm">Admitted</p><p className="text-2xl font-bold text-blue-600">{stats.admittedPatients}</p></div>
          <div className="stat-card"><p className="text-gray-500 text-sm">Total Patients</p><p className="text-2xl font-bold text-gray-600">{stats.totalPatients}</p></div>
          <div className="stat-card"><p className="text-gray-500 text-sm">Deliveries</p><p className="text-2xl font-bold text-green-600">{stats.totalDeliveries}</p></div>
          <div className="stat-card"><p className="text-gray-500 text-sm">Today</p><p className="text-lg font-bold text-gray-600">{new Date().toLocaleDateString()}</p></div>
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
              <span>{tab.icon}</span> {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'patients' && <PatientManagement />}
        {activeTab === 'medical' && <MedicalRecordsTab />}
        {activeTab === 'babies' && <BabyRecordsTab />}
        {activeTab === 'labreports' && <LabReportsView />}
        {activeTab === 'outdoor' && <OutdoorPatientPrescription />}
      </div>
    </div>
  );
};

const PatientManagement = () => {
  const { patients, bills, birthRecords, medicalRecords, addPatient, updatePatient, dischargePatient, requestDischarge, getPatientDues } = useData();
  const { apiPatients } = useApiPatients(); // keep for fallback
  const { admittedPatients: apiAdmitted, loading: apiLoading, error: apiError, refetch } = useApiAdmittedPatients();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  const [dischargeModal, setDischargeModal] = useState(null);
  const [dischargeCondition, setDischargeCondition] = useState({ patientCondition: '', babyCondition: '' });

const [form, setForm] = useState({ 
  name: '', husbandName: '', fatherName: '', age: '', phone: '', address: '', room: '', 
  diseases: '', indication: '', pog: '', weight: '', doctorName: DOCTOR_INFO.name, 
  procedure: '', staffName: '', notes: '', cnic: '', bloodGroup: '', allergies: '', 
  emergencyContact: '', emergencyPhone: '', referralSource: '', previousHistory: '', 
  generalConsent: false, aspiScore: '', admitOn: '',
  // New fields
  lmp: '', dateOfDischarge: '',
  chiefComplaints: '', historyOfPresentIllness: '', pastHistory: '', obsHistory: '',
  gynaecologicalHistory: '', allergiesBackground: '',
  anaemia: '', jaundice: '', clubbing: '', cyanosis: '', dehydration: '',
  bp: '', pulse: '', tempEdemaResp: '',
  cvs: '', cns: '', resp: '', git: '', provisionalDiagnosis: '',
  cbcHb: '', plateletCount: '', hbsAg: '', hcv: '', rbs: '', pt: '', aptt: '', sUricAcid: '',
  serumBilirubin: '', alkalinePhosphatase: '', alt: '', sUrea: '', sCreatinine: '', urineProtein24hr: '',
  ultrasound: '', urineRE: '', echo: '',
  operationName: '', operativeFindings: '', operationComplication: '', biopsy: '',
  anaesthesiaType: [], anaesthetist: '', anaesthesiaComplications: '', anaesthesiaNotes: '',
  recoveryNotes: '', postOpInstructions: '',
  babyGender: '', babyApgar: '', babyDeliveryWeight: '', babyCry: ''
});

const procedures = [
  'NVD', 'C-Section', 'Hysterectomy', 'Myomectomy', 'Laparoscopy', 'Hysteroscopy', 'D&C',
  'A-P Repair', 'Bartholin Cyst', 'Vulvectomy', 'Ectopic Pregnancy', 'Tubal Ligation',
  'Repair of Perineal Tears', 'Manchester Repair', 'Abdominal Suspension Procedure for Uterovaginal Prolapse',
  'Sacrohysteropexy', 'Sacrocolpopexy', 'Colpoclesis', 'Sacrospinous Fixation',
  'Burch Colposuspension', 'TVT', 'Perineal Tightening Procedures',
  'Urinary and Faecal Fistula Repair', 'Laproscopic Hysterectomy',
  'EmLSCS', 'ElLSCS', 'Vaginal Hysterectomy', 'Total Abdominal Hysterectomy',
  'Suction Curettage', 'Laprotomy', 'Hystrotomy', 'Intrauterine Insemination',
  'TAH/BSO', 'NVD+C-Sec', 'Obs', 'Other'
];
  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const formatCNIC = (value) => {
  // Strip everything except digits
  const digits = value.replace(/\D/g, '').slice(0, 13);
  let formatted = digits;
  if (digits.length > 5) {
    formatted = digits.slice(0, 5) + '-' + digits.slice(5);
  }
  if (digits.length > 12) {
    formatted = digits.slice(0, 5) + '-' + digits.slice(5, 12) + '-' + digits.slice(12);
  }
  return formatted;
};
 const [babySubmitting, setBabySubmitting] = useState(false);

 const [patientSubmitting, setPatientSubmitting] = useState(false);
const [dischargeSubmitting, setDischargeSubmitting] = useState(false);
 

const handleSubmit = async (e) => {
    e.preventDefault();

    if (editingId) {
      setPatientSubmitting(true);
      const API_TOKEN = 'A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2';
      const BASE_URL = process.env.NODE_ENV === 'production' ? 'https://api.ruqqiasultanaclinic.com' : '';

      const updatePayload = {
        id: Number(editingId),
        name: form.name || '',
        fatherName: form.fatherName || '',
        phone: form.phone || '',
        status: form.status || 'admitted',
        totalBill: Number(form.totalBill) || 0,
        paid: Number(form.paid) || 0,
        due: Number(form.due) || 0,
        cnic: form.cnic || '',
        husband: form.husbandName || '',
        age: Number(form.age) || 0,
        bloodGroup: form.bloodGroup || '',
        address: form.address || '',
        room: form.room || '',
        admissionDate: form.admitOn ? new Date(form.admitOn).toISOString() : new Date().toISOString(),
        dischargeDate: new Date().toISOString(),
        diagnosis: form.diseases || '',
        procedure: form.procedure || '',
        doctor: form.doctorName || '',
        babyName: form.babyName || '',
        babySex: form.babySex || '',
        babyDOB: new Date().toISOString(),
        babyWeight: Number(form.babyWeight) || 0,
        babyMode: form.babyMode || '',
        babyCertificate: form.babyCertificate || '',
        pateintAdmitOn: form.admitOn ? new Date(form.admitOn).toISOString() : new Date().toISOString(),
        mrNo: form.mrNo || '',
        // New fields
        lmp: form.lmp ? new Date(form.lmp).toISOString() : null,
        dateOfDischarge: form.dateOfDischarge ? new Date(form.dateOfDischarge).toISOString() : null,
        chiefComplaints: form.chiefComplaints || '',
        historyOfPresentIllness: form.historyOfPresentIllness || '',
        pastHistory: form.pastHistory || '',
        obsHistory: form.obsHistory || '',
        gynaecologicalHistory: form.gynaecologicalHistory || '',
        allergiesBackground: form.allergiesBackground || '',
        anaemia: form.anaemia || '',
        jaundice: form.jaundice || '',
        clubbing: form.clubbing || '',
        cyanosis: form.cyanosis || '',
        dehydration: form.dehydration || '',
        bp: form.bp || '',
        pulse: form.pulse || '',
        tempEdemaResp: form.tempEdemaResp || '',
        cvs: form.cvs || '',
        cns: form.cns || '',
        resp: form.resp || '',
        git: form.git || '',
        provisionalDiagnosis: form.provisionalDiagnosis || '',
        cbcHb: form.cbcHb || '',
        plateletCount: form.plateletCount || '',
        hbsAg: form.hbsAg || '',
        hcv: form.hcv || '',
        rbs: form.rbs || '',
        pt: form.pt || '',
        aptt: form.aptt || '',
        sUricAcid: form.sUricAcid || '',
        serumBilirubin: form.serumBilirubin || '',
        alkalinePhosphatase: form.alkalinePhosphatase || '',
        alt: form.alt || '',
        sUrea: form.sUrea || '',
        sCreatinine: form.sCreatinine || '',
        urineProtein24hr: form.urineProtein24hr || '',
        ultrasound: form.ultrasound || '',
        urineRE: form.urineRE || '',
        echo: form.echo || '',
        operationName: form.operationName || '',
        operativeFindings: form.operativeFindings || '',
        operationComplication: form.operationComplication || '',
        biopsy: form.biopsy || '',
        anaesthesiaType: Array.isArray(form.anaesthesiaType) ? form.anaesthesiaType.join(',') : (form.anaesthesiaType || ''),
        anaesthetist: form.anaesthetist || '',
        anaesthesiaComplications: form.anaesthesiaComplications || '',
        anaesthesiaNotes: form.anaesthesiaNotes || '',
        recoveryNotes: form.recoveryNotes || '',
        postOpInstructions: form.postOpInstructions || '',
        babyGender: form.babyGender || '',
        babyApgar: form.babyApgar || '',
        babyDeliveryWeight: form.babyDeliveryWeight || '',
       babyCry: form.babyCry || '',
  // ✅ NEW FIELDS
  weightKg: form.weight || '',
  emergencyContact: form.emergencyContact || '',
  emergencyPhone: form.emergencyPhone || '',
  allergies: form.allergies || '',
  referralSource: form.referralSource || '',
  medicalHistory: form.previousHistory || '',
  indication: form.indication || '',
  pog: form.pog || '',
  apgarScore: form.aspiScore || '',
  staff: form.staffName || '',
  notes: form.notes || '',
};

      try {
        const res = await fetch(`${BASE_URL}/api/Patient/patientdetails/update/${editingId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'accept': '*/*',
            'X-API-TOKEN': API_TOKEN,
          },
          body: JSON.stringify(updatePayload),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const result = await res.json();
        console.log('Patient updated via API:', result);
        alert('Patient updated successfully!');
        refetch(); // refresh the admitted patients list
      } catch (err) {
        alert('Failed to update patient: ' + err.message);
        setPatientSubmitting(false);
        return;
      }
      updatePatient(editingId, form); // also update local state
      setPatientSubmitting(false);
      resetForm();
      return;
    }

    setPatientSubmitting(true);
    setBabySubmitting(true);

    // Build API payload for new patient
  const apiPayload = {
  id: 0,
  name: form.name || '',
  fatherName: form.fatherName || '',
  phone: form.phone || '',
  status: 'admitted',
  totalBill: 0,
  paid: 0,
  due: 0,
  cnic: form.cnic || '',
  husband: form.husbandName || '',
  age: Number(form.age) || 0,
  bloodGroup: form.bloodGroup || '',
  address: form.address || '',
  room: form.room || '',
  admissionDate: form.admitOn ? new Date(form.admitOn).toISOString() : new Date().toISOString(),
  pateintAdmitOn: form.admitOn ? new Date(form.admitOn).toISOString() : new Date().toISOString(),
  dischargeDate: new Date().toISOString(),
  diagnosis: form.diseases || '',
  procedure: form.procedure || '',
  doctor: form.doctorName || '',
  babyName: '',
  babySex: '',
  babyDOB: new Date().toISOString(),
  babyWeight: 0,
  babyMode: '',
  babyCertificate: '',
  mrNo: '',
  // ── New fields ──────────────────────────────────────
  lmp: form.lmp ? new Date(form.lmp).toISOString() : null,
  dateOfDischarge: form.dateOfDischarge ? new Date(form.dateOfDischarge).toISOString() : null,
  // History & Examination
  chiefComplaints: form.chiefComplaints || '',
  historyOfPresentIllness: form.historyOfPresentIllness || '',
  pastHistory: form.pastHistory || '',
  obsHistory: form.obsHistory || '',
  gynaecologicalHistory: form.gynaecologicalHistory || '',
  allergiesBackground: form.allergiesBackground || '',
  // General Examination
  anaemia: form.anaemia || '',
  jaundice: form.jaundice || '',
  clubbing: form.clubbing || '',
  cyanosis: form.cyanosis || '',
  dehydration: form.dehydration || '',
  bp: form.bp || '',
  pulse: form.pulse || '',
  tempEdemaResp: form.tempEdemaResp || '',
  // Systemic Examination
  cvs: form.cvs || '',
  cns: form.cns || '',
  resp: form.resp || '',
  git: form.git || '',
  provisionalDiagnosis: form.provisionalDiagnosis || '',
  // Investigations - Haematology
  cbcHb: form.cbcHb || '',
  plateletCount: form.plateletCount || '',
  hbsAg: form.hbsAg || '',
  hcv: form.hcv || '',
  rbs: form.rbs || '',
  pt: form.pt || '',
  aptt: form.aptt || '',
  sUricAcid: form.sUricAcid || '',
  // LFTs & Renal
  serumBilirubin: form.serumBilirubin || '',
  alkalinePhosphatase: form.alkalinePhosphatase || '',
  alt: form.alt || '',
  sUrea: form.sUrea || '',
  sCreatinine: form.sCreatinine || '',
  urineProtein24hr: form.urineProtein24hr || '',
  // Imaging
  ultrasound: form.ultrasound || '',
  urineRE: form.urineRE || '',
  echo: form.echo || '',
  // Operation
  operationName: form.operationName || '',
  operativeFindings: form.operativeFindings || '',
  operationComplication: form.operationComplication || '',
  biopsy: form.biopsy || '',
  // Anaesthesia
  anaesthesiaType: Array.isArray(form.anaesthesiaType) ? form.anaesthesiaType.join(',') : '',
  anaesthetist: form.anaesthetist || '',
  anaesthesiaComplications: form.anaesthesiaComplications || '',
  anaesthesiaNotes: form.anaesthesiaNotes || '',
  recoveryNotes: form.recoveryNotes || '',
  postOpInstructions: form.postOpInstructions || '',
  // Baby Details
  babyGender: form.babyGender || '',
  babyApgar: form.babyApgar || '',
  babyDeliveryWeight: form.babyDeliveryWeight || '',
 babyCry: form.babyCry || '',
  // ✅ NEW FIELDS
  weightKg: form.weight || '',
  emergencyContact: form.emergencyContact || '',
  emergencyPhone: form.emergencyPhone || '',
  allergies: form.allergies || '',
  referralSource: form.referralSource || '',
  medicalHistory: form.previousHistory || '',
  indication: form.indication || '',
  pog: form.pog || '',
  apgarScore: form.aspiScore || '',
  staff: form.staffName || '',
  notes: form.notes || '',
};

  try {
      const result = await createPatient(apiPayload);
      console.log('Patient saved to API:', result);
    } catch (err) {
      setPatientSubmitting(false);
      alert('Failed to save patient: ' + err.message);
      return;
    }
    // Always save locally
   // Always save locally
    setPatientSubmitting(false);
    resetForm();
  };

 const resetForm = () => {
    setForm({ 
      name: '', husbandName: '', fatherName: '', age: '', phone: '', address: '', room: '', 
      diseases: '', indication: '', pog: '', weight: '', doctorName: DOCTOR_INFO.name, 
      procedure: '', staffName: '', notes: '', cnic: '', bloodGroup: '', allergies: '', 
      emergencyContact: '', emergencyPhone: '', referralSource: '', previousHistory: '', 
      generalConsent: false, aspiScore: '', admitOn: '',
      lmp: '', dateOfDischarge: '',
      chiefComplaints: '', historyOfPresentIllness: '', pastHistory: '', obsHistory: '',
      gynaecologicalHistory: '', allergiesBackground: '',
      anaemia: '', jaundice: '', clubbing: '', cyanosis: '', dehydration: '',
      bp: '', pulse: '', tempEdemaResp: '',
      cvs: '', cns: '', resp: '', git: '', provisionalDiagnosis: '',
      cbcHb: '', plateletCount: '', hbsAg: '', hcv: '', rbs: '', pt: '', aptt: '', sUricAcid: '',
      serumBilirubin: '', alkalinePhosphatase: '', alt: '', sUrea: '', sCreatinine: '', urineProtein24hr: '',
      ultrasound: '', urineRE: '', echo: '',
      operationName: '', operativeFindings: '', operationComplication: '', biopsy: '',
      anaesthesiaType: [], anaesthetist: '', anaesthesiaComplications: '', anaesthesiaNotes: '',
      recoveryNotes: '', postOpInstructions: '',
      babyGender: '', babyApgar: '', babyDeliveryWeight: '', babyCry: ''
    });
    setEditingId(null);
    setShowForm(false);
  };
const handleEdit = (p) => { 
  setForm({
    // ── Basic identity ──────────────────────────────────────────────
    name:             p.name             || '',
    husbandName:      p.husband          || p.husbandName      || '',
    fatherName:       p.fatherName       || '',
    age:              p.age              || '',
    phone:            p.phone            || '',
    cnic:             p.cnic             || '',
    bloodGroup:       p.bloodGroup       || '',
    address:          p.address          || '',
    room:             p.room             || '',
    mrNo:             p.mrNo             || '',
    status:           p.status           || 'admitted',
    totalBill:        p.totalBill        || 0,
    paid:             p.paid             || 0,
    due:              p.due              || 0,

    // ── Clinical ────────────────────────────────────────────────────
    diseases:         p.diagnosis        || p.diseases         || '',
    indication:       p.indication       || '',
    pog:              p.pog              || '',
    weight:           p.weight           || '',
    doctorName:       p.doctor           || p.doctorName       || DOCTOR_INFO.name,
    procedure:        p.procedure        || '',
    staffName:        p.staffName        || '',
    notes:            p.notes            || '',
    allergies:        p.allergies        || '',
    emergencyContact: p.emergencyContact || '',
    emergencyPhone:   p.emergencyPhone   || '',
    referralSource:   p.referralSource   || '',
    previousHistory:  p.previousHistory  || '',
    generalConsent:   p.generalConsent   || false,
    aspiScore:        p.aspiScore        || '',

    // ── Dates ───────────────────────────────────────────────────────
    admitOn: p.pateintAdmitOn
  ? p.pateintAdmitOn.slice(0, 16)
  : p.admitOn || '',
    lmp: p.lmp
      ? new Date(p.lmp).toISOString().split('T')[0]
      : '',
    dateOfDischarge: p.dateOfDischarge
      ? new Date(p.dateOfDischarge).toISOString().split('T')[0]
      : '',

    // ── History & Examination ───────────────────────────────────────
    chiefComplaints:          p.chiefComplaints          || '',
    historyOfPresentIllness:  p.historyOfPresentIllness  || '',
    pastHistory:              p.pastHistory              || '',
    obsHistory:               p.obsHistory               || '',
    gynaecologicalHistory:    p.gynaecologicalHistory    || '',
    allergiesBackground:      p.allergiesBackground      || '',

    // ── General Examination ─────────────────────────────────────────
    anaemia:      p.anaemia      || '',
    jaundice:     p.jaundice     || '',
    clubbing:     p.clubbing     || '',
    cyanosis:     p.cyanosis     || '',
    dehydration:  p.dehydration  || '',
    bp:           p.bp           || '',
    pulse:        p.pulse        || '',
    tempEdemaResp: p.tempEdemaResp || '',

    // ── Systemic Examination ────────────────────────────────────────
    cvs:                  p.cvs                  || '',
    cns:                  p.cns                  || '',
    resp:                 p.resp                 || '',
    git:                  p.git                  || '',
    provisionalDiagnosis: p.provisionalDiagnosis || '',

    // ── Investigations – Haematology ────────────────────────────────
    cbcHb:              p.cbcHb              || '',
    plateletCount:      p.plateletCount      || '',
    hbsAg:              p.hbsAg              || '',
    hcv:                p.hcv                || '',
    rbs:                p.rbs                || '',
    pt:                 p.pt                 || '',
    aptt:               p.aptt               || '',
    sUricAcid:          p.sUricAcid          || '',

    // ── LFTs & Renal ────────────────────────────────────────────────
    serumBilirubin:      p.serumBilirubin      || '',
    alkalinePhosphatase: p.alkalinePhosphatase || '',
    alt:                 p.alt                 || '',
    sUrea:               p.sUrea               || '',
    sCreatinine:         p.sCreatinine         || '',
    urineProtein24hr:    p.urineProtein24hr    || '',

    // ── Imaging ─────────────────────────────────────────────────────
    ultrasound: p.ultrasound || '',
    urineRE:    p.urineRE    || '',
    echo:       p.echo       || '',

    // ── Operation ───────────────────────────────────────────────────
    operationName:        p.operationName        || '',
    operativeFindings:    p.operativeFindings    || '',
    operationComplication: p.operationComplication || '',
    biopsy:               p.biopsy               || '',

    // ── Anaesthesia ─────────────────────────────────────────────────
    anaesthesiaType: Array.isArray(p.anaesthesiaType)
      ? p.anaesthesiaType
      : (typeof p.anaesthesiaType === 'string' && p.anaesthesiaType
          ? p.anaesthesiaType.split(',')
          : []),
    anaesthetist:            p.anaesthetist            || '',
    anaesthesiaComplications: p.anaesthesiaComplications || '',
    anaesthesiaNotes:        p.anaesthesiaNotes        || '',
    recoveryNotes:           p.recoveryNotes           || '',
    postOpInstructions:      p.postOpInstructions      || '',

    // ── Baby Details ────────────────────────────────────────────────
    babyGender:        p.babyGender        || '',
    babyApgar:         p.babyApgar         || '',
    babyDeliveryWeight: p.babyDeliveryWeight || '',
   babyCry:           p.babyCry           || '',
    // ✅ NEW FIELDS
    weight:           p.weightKg           || '',
    emergencyContact: p.emergencyContact   || '',
    emergencyPhone:   p.emergencyPhone     || '',
    allergies:        p.allergies          || '',
    referralSource:   p.referralSource     || '',
    previousHistory:  p.medicalHistory     || '',
    indication:       p.indication         || '',
    pog:              p.pog                || '',
    aspiScore:        p.apgarScore         || '',
    staffName:        p.staff              || '',
    notes:            p.notes              || '',
  });
  setEditingId(p.id); 
  setShowForm(true); 
};
  const handleDischargeClick = (patient) => {
    if (patient.dischargeRequested) {
      alert('Discharge already requested. Waiting for Admin approval.');
      return;
    }
    setDischargeCondition({ patientCondition: '', babyCondition: '' });
    setDischargeModal(patient);
  };

  const getPatientMedicalRecords = (patientId) => medicalRecords.filter(r => r.patientId === patientId);
  const getPatientBaby = (patientId) => birthRecords.find(b => b.patientId === patientId);
  const getPatientBills = (patientId) => bills.filter(b => b.patientId === patientId);

  const printDischargeSummary = (patient) => {
    const records = getPatientMedicalRecords(patient.id);
    const baby = getPatientBaby(patient.id);
    const patientBills = getPatientBills(patient.id);
    const totalBilled = patientBills.reduce((sum, b) => sum + Number(b.totalAmount || 0), 0);
    const totalPaid = patientBills.reduce((sum, b) => sum + Number(b.paidAmount || 0), 0);

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Discharge Summary - ${patient.mrNo}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 30px; background: white; font-size: 12px; }
          .header { text-align: center; border-bottom: 2px solid #1e40af; padding-bottom: 15px; margin-bottom: 20px; }
          .logo { width: 60px; height: 60px; margin: 0 auto 5px; }
          .clinic-name { font-size: 18px; font-weight: bold; color: #1e40af; }
          .address { font-size: 10px; color: #666; }
          .title { font-size: 16px; font-weight: bold; color: #1e40af; text-align: center; margin: 15px 0; background: #e3f2fd; padding: 8px; }
          .section { margin-bottom: 15px; }
          .section-title { font-weight: bold; color: #1e40af; border-bottom: 1px solid #ccc; padding-bottom: 3px; margin-bottom: 8px; font-size: 13px; }
          .row { display: flex; margin-bottom: 5px; }
          .label { width: 140px; font-weight: bold; color: #555; }
          .value { flex: 1; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 11px; }
          th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
          th { background: #f5f5f5; font-weight: bold; }
          .baby-section { background: #fff3e0; padding: 10px; border-radius: 5px; margin-top: 10px; }
          .signatures { display: flex; justify-content: space-between; margin-top: 40px; }
          .sig-box { text-align: center; }
          .sig-line { border-top: 1px solid #000; width: 120px; margin-bottom: 3px; }
          .total-row { font-weight: bold; background: #e8f5e9; }
          @media print { body { padding: 15px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="clinic-name">${DOCTOR_INFO.clinicName}</div>
          <div class="address">${DOCTOR_INFO.address} | Phone: ${DOCTOR_INFO.phone}</div>
        </div>
        
        <div class="title">DISCHARGE SUMMARY</div>
        
        <div class="section">
          <div class="section-title">Patient Information</div>
          <div class="row"><span class="label">MR Number:</span><span class="value">${patient.mrNo}</span></div>
          <div class="row"><span class="label">Patient Name:</span><span class="value">${patient.name}</span></div>
          <div class="row"><span class="label">Husband/Father:</span><span class="value">${patient.husbandName || patient.fatherName || '-'}</span></div>
          <div class="row"><span class="label">Age:</span><span class="value">${patient.age || '-'}</span></div>
          <div class="row"><span class="label">Phone:</span><span class="value">${patient.phone || '-'}</span></div>
          <div class="row"><span class="label">Address:</span><span class="value">${patient.address || '-'}</span></div>
          <div class="row"><span class="label">Room:</span><span class="value">${patient.room || '-'}</span></div>
        </div>

        <div class="section">
          <div class="section-title">Admission Details</div>
          <div class="row"><span class="label">Admission Date:</span><span class="value">${patient.admissionDate} at ${patient.admissionTime}</span></div>
          <div class="row"><span class="label">Discharge Date:</span><span class="value">${new Date().toISOString().split('T')[0]} at ${new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span></div>
          <div class="row"><span class="label">Diagnosis:</span><span class="value">${patient.diseases || '-'}</span></div>
          <div class="row"><span class="label">Indication:</span><span class="value">${patient.indication || '-'}</span></div>
          <div class="row"><span class="label">POG:</span><span class="value">${patient.pog || '-'}</span></div>
          <div class="row"><span class="label">Procedure:</span><span class="value">${patient.procedure || '-'}</span></div>
          <div class="row"><span class="label">Doctor:</span><span class="value">${patient.doctorName || DOCTOR_INFO.name}</span></div>
        </div>

        ${records.length > 0 ? `
        <div class="section">
          <div class="section-title">Medical Records & Treatment</div>
          <table>
            <thead><tr><th>Date/Time</th><th>Type</th><th>Details</th><th>Medications</th></tr></thead>
            <tbody>
              ${records.map(r => `
                <tr>
                  <td>${r.date} ${r.time}</td>
                  <td>${r.type}</td>
                  <td>${r.description || r.operationName || r.deliveryType || '-'}</td>
                  <td>${r.medication ? r.medication + ' ' + (r.dosage || '') : '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}

        ${baby ? `
        <div class="section">
          <div class="section-title">Baby Details (Delivery)</div>
          <div class="baby-section">
            <div class="row"><span class="label">Baby Name:</span><span class="value">${baby.babyName || 'Baby of ' + baby.motherName}</span></div>
            <div class="row"><span class="label">Sex:</span><span class="value">${baby.sex}</span></div>
            <div class="row"><span class="label">Date of Birth:</span><span class="value">${baby.dateOfBirth} at ${baby.timeOfBirth} ${baby.amPm}</span></div>
            <div class="row"><span class="label">Birth Weight:</span><span class="value">${baby.birthWeight} kg</span></div>
            <div class="row"><span class="label">Mode of Delivery:</span><span class="value">${baby.modeOfDelivery}</span></div>
            <div class="row"><span class="label">NICU Stay:</span><span class="value">${baby.nicuStay || '0'}</span></div>
            <div class="row"><span class="label">Certificate No:</span><span class="value">${baby.certificateNo}</span></div>
          </div>
        </div>
        ` : ''}

        <div class="section">
          <div class="section-title">Billing Summary</div>
          <table>
            <thead><tr><th>Date</th><th>Description</th><th>Amount</th><th>Paid</th></tr></thead>
            <tbody>
              ${patientBills.map(b => `
                <tr>
                  <td>${b.date}</td>
                  <td>${b.items?.map(i => i.description).join(', ') || '-'}</td>
                  <td>Rs. ${Number(b.totalAmount).toLocaleString()}</td>
                  <td>Rs. ${Number(b.paidAmount || 0).toLocaleString()}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="2"><strong>TOTAL</strong></td>
                <td><strong>Rs. ${totalBilled.toLocaleString()}</strong></td>
                <td><strong>Rs. ${totalPaid.toLocaleString()}</strong></td>
              </tr>
            </tbody>
          </table>
          <div class="row" style="margin-top:10px;"><span class="label">Balance Due:</span><span class="value" style="color: ${totalBilled - totalPaid > 0 ? 'red' : 'green'}; font-weight: bold;">Rs. ${(totalBilled - totalPaid).toLocaleString()}</span></div>
        </div>

        <div class="signatures">
          <div class="sig-box"><div class="sig-line"></div><div>Patient/Attendant</div></div>
          <div class="sig-box"><div class="sig-line"></div><div>Nurse</div></div>
          <div class="sig-box"><div class="sig-line"></div><div>Doctor</div></div>
        </div>

        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const confirmDischarge = async () => {
  if (!dischargeCondition.patientCondition) {
    alert('Please enter patient condition at discharge');
    return;
  }
  setDischargeSubmitting(true);

  // Build API payload
  const payload = {
    id: 0,
    patientId: Number(dischargeModal.id) || 0,
    mrNo: dischargeModal.mrNo || '',
    patientCondition: dischargeCondition.patientCondition,
    babyCondition: dischargeCondition.babyCondition || '',
    isRequestForDischarge: true,
    isAllowed: false,       // Admin hasn't approved yet
    isPaymentDone: false,   // Admin will verify payment
    createdDate: new Date().toISOString(),
  };

  try {
    const result = await createDischargeRequest(payload);
    console.log('Discharge request sent to API:', result);
  } catch (err) {
    console.warn('API discharge request failed, saving locally only:', err.message);
  }

  // Always update local state
  requestDischarge(dischargeModal.id, {
    dischargePatientCondition: dischargeCondition.patientCondition,
    dischargeBabyCondition: dischargeCondition.babyCondition,
    dischargeRequestedBy: 'Doctor',
    dischargeRequestedAt: new Date().toISOString(),
  });

  alert('Discharge request sent to Admin for approval.\n\nAdmin will review and approve after verifying all dues are cleared.');
  setDischargeModal(null);
  setDischargeCondition({ patientCondition: '', babyCondition: '' });
  setDischargeSubmitting(false);
};
 // Use API patients if available, map to local structure
// Use admitted-only API endpoint, fallback to local
 const allPatients = apiAdmitted.length > 0
    ? apiAdmitted.map(p => ({
        // ── Spread ALL raw API fields first so nothing is lost ──
        ...p,
        // ── Then override with display-friendly mapped fields ──
        id: p.id,
        name: p.name && p.name !== 'string' ? p.name : 'N/A',
        mrNo: p.mrNo || `MR-${p.id}`,
        husbandName: p.husband && p.husband !== 'string' ? p.husband : '',
        fatherName: p.fatherName && p.fatherName !== 'string' ? p.fatherName : '',
        phone: p.phone && p.phone !== 'string' ? p.phone : '',
        cnic: p.cnic && p.cnic !== 'string' ? p.cnic : '',
        age: p.age || '',
        bloodGroup: p.bloodGroup && p.bloodGroup !== 'string' ? p.bloodGroup : '',
        address: p.address && p.address !== 'string' ? p.address : '',
        room: p.room && p.room !== 'string' ? p.room : '',
        admissionDate: p.admissionDate ? new Date(p.admissionDate).toLocaleDateString() : '',
        admissionTime: p.admissionDate ? new Date(p.admissionDate).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : '',
        diseases: p.diagnosis && p.diagnosis !== 'string' ? p.diagnosis : '',
        procedure: p.procedure && p.procedure !== 'string' ? p.procedure : '',
        doctorName: p.doctor && p.doctor !== 'string' ? p.doctor : '',
        status: p.status && p.status !== 'string' ? p.status : 'admitted',
        dischargeRequested: false,
        _fromApi: true,
      }))
    : patients;
// Since this API returns only admitted patients, all are admitted
  const admitted = allPatients.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.mrNo?.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(admitted.length / ITEMS_PER_PAGE);
  const paginatedAdmitted = admitted.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Discharged still comes from local data
  const discharged = patients.filter(p => p.status === 'discharged').slice(0, 10);
  const pendingDischarge = allPatients.filter(p => p.dischargeRequested);
const printPatientDetail = (p) => {
  const printWindow = window.open('', '_blank');
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Patient Detail - ${p.mrNo}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; padding: 30px; font-size: 12px; color: #333; }
        .header { text-align: center; border-bottom: 3px solid #1e40af; padding-bottom: 15px; margin-bottom: 20px; }
        .clinic-name { font-size: 20px; font-weight: bold; color: #1e40af; }
        .subtitle { font-size: 12px; color: #555; margin: 2px 0; }
        .title { font-size: 16px; font-weight: bold; color: #1e40af; text-align: center; margin: 15px 0; background: #e3f2fd; padding: 8px; border-radius: 4px; }
        .section { margin-bottom: 16px; border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden; }
        .section-title { background: #dbeafe; color: #1e40af; padding: 7px 12px; font-weight: bold; font-size: 13px; }
        .section-body { padding: 10px 12px; }
        .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
        .grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; }
        .grid4 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 6px; }
        .field { margin-bottom: 4px; }
        .label { color: #888; font-size: 10px; display: block; }
        .value { font-size: 12px; font-weight: 500; }
        .signatures { display: flex; justify-content: space-between; margin-top: 50px; }
        .sig-box { text-align: center; }
        .sig-line { border-top: 1px solid #000; width: 130px; margin: 0 auto 4px; }
        @media print { body { padding: 15px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="clinic-name">${DOCTOR_INFO.clinicName}</div>
        <div class="subtitle">${DOCTOR_INFO.address}</div>
        <div class="subtitle">Phone: ${DOCTOR_INFO.phone}</div>
      </div>

      <div class="title">PATIENT DETAIL SHEET</div>

      <div class="section">
        <div class="section-title">Basic Information</div>
        <div class="section-body grid2">
          <div class="field"><span class="label">MR No</span><span class="value">${p.mrNo || '—'}</span></div>
          <div class="field"><span class="label">Patient Name</span><span class="value">${p.name || '—'}</span></div>
          <div class="field"><span class="label">Husband Name</span><span class="value">${p.husbandName || p.husband || '—'}</span></div>
          <div class="field"><span class="label">Father Name</span><span class="value">${p.fatherName || '—'}</span></div>
          <div class="field"><span class="label">Age</span><span class="value">${p.age || '—'}</span></div>
          <div class="field"><span class="label">Blood Group</span><span class="value">${p.bloodGroup || '—'}</span></div>
          <div class="field"><span class="label">CNIC</span><span class="value">${p.cnic || '—'}</span></div>
          <div class="field"><span class="label">Phone</span><span class="value">${p.phone || '—'}</span></div>
          <div class="field"><span class="label">Address</span><span class="value">${p.address || '—'}</span></div>
          <div class="field"><span class="label">Room</span><span class="value">${p.room || '—'}</span></div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Admission Details</div>
        <div class="section-body grid2">
          <div class="field"><span class="label">Admission Date</span><span class="value">${p.admissionDate || '—'} ${p.admissionTime || ''}</span></div>
          <div class="field"><span class="label">LMP</span><span class="value">${p.lmp ? new Date(p.lmp).toLocaleDateString() : '—'}</span></div>
          <div class="field"><span class="label">Diagnosis</span><span class="value">${p.diseases || p.diagnosis || '—'}</span></div>
          <div class="field"><span class="label">Procedure</span><span class="value">${p.procedure || '—'}</span></div>
          <div class="field"><span class="label">Doctor</span><span class="value">${p.doctorName || p.doctor || '—'}</span></div>
          <div class="field"><span class="label">POG</span><span class="value">${p.pog || '—'}</span></div>
          <div class="field"><span class="label">Provisional Diagnosis</span><span class="value">${p.provisionalDiagnosis || '—'}</span></div>
          <div class="field"><span class="label">Status</span><span class="value">${p.status || '—'}</span></div>
        </div>
      </div>

      ${(p.chiefComplaints || p.historyOfPresentIllness || p.pastHistory || p.obsHistory || p.gynaecologicalHistory) ? `
      <div class="section">
        <div class="section-title">History & Examination</div>
        <div class="section-body grid2">
          ${p.chiefComplaints ? `<div class="field"><span class="label">Chief Complaints</span><span class="value">${p.chiefComplaints}</span></div>` : ''}
          ${p.historyOfPresentIllness ? `<div class="field"><span class="label">History of Present Illness</span><span class="value">${p.historyOfPresentIllness}</span></div>` : ''}
          ${p.pastHistory ? `<div class="field"><span class="label">Past History</span><span class="value">${p.pastHistory}</span></div>` : ''}
          ${p.obsHistory ? `<div class="field"><span class="label">Obs. History</span><span class="value">${p.obsHistory}</span></div>` : ''}
          ${p.gynaecologicalHistory ? `<div class="field"><span class="label">Gynaecological History</span><span class="value">${p.gynaecologicalHistory}</span></div>` : ''}
          ${p.allergiesBackground ? `<div class="field"><span class="label">Allergies & Background Meds</span><span class="value">${p.allergiesBackground}</span></div>` : ''}
        </div>
      </div>
      ` : ''}

      ${(p.bp || p.pulse || p.anaemia || p.jaundice) ? `
      <div class="section">
        <div class="section-title">General & Systemic Examination</div>
        <div class="section-body grid4">
          ${p.bp ? `<div class="field"><span class="label">B/P</span><span class="value">${p.bp}</span></div>` : ''}
          ${p.pulse ? `<div class="field"><span class="label">Pulse</span><span class="value">${p.pulse}</span></div>` : ''}
          ${p.tempEdemaResp ? `<div class="field"><span class="label">Temp/Edema/Resp</span><span class="value">${p.tempEdemaResp}</span></div>` : ''}
          ${p.anaemia ? `<div class="field"><span class="label">Anaemia</span><span class="value">${p.anaemia}</span></div>` : ''}
          ${p.jaundice ? `<div class="field"><span class="label">Jaundice</span><span class="value">${p.jaundice}</span></div>` : ''}
          ${p.clubbing ? `<div class="field"><span class="label">Clubbing</span><span class="value">${p.clubbing}</span></div>` : ''}
          ${p.cyanosis ? `<div class="field"><span class="label">Cyanosis</span><span class="value">${p.cyanosis}</span></div>` : ''}
          ${p.dehydration ? `<div class="field"><span class="label">Dehydration</span><span class="value">${p.dehydration}</span></div>` : ''}
          ${p.cvs ? `<div class="field"><span class="label">CVS</span><span class="value">${p.cvs}</span></div>` : ''}
          ${p.cns ? `<div class="field"><span class="label">CNS</span><span class="value">${p.cns}</span></div>` : ''}
          ${p.resp ? `<div class="field"><span class="label">Resp</span><span class="value">${p.resp}</span></div>` : ''}
          ${p.git ? `<div class="field"><span class="label">GIT</span><span class="value">${p.git}</span></div>` : ''}
        </div>
      </div>
      ` : ''}

      ${(p.cbcHb || p.hbsAg || p.rbs || p.serumBilirubin || p.ultrasound) ? `
      <div class="section">
        <div class="section-title">Investigations</div>
        <div class="section-body grid4">
          ${p.cbcHb ? `<div class="field"><span class="label">CBC/Hb%</span><span class="value">${p.cbcHb}</span></div>` : ''}
          ${p.plateletCount ? `<div class="field"><span class="label">Platelet Count</span><span class="value">${p.plateletCount}</span></div>` : ''}
          ${p.hbsAg ? `<div class="field"><span class="label">HbsAg</span><span class="value">${p.hbsAg}</span></div>` : ''}
          ${p.hcv ? `<div class="field"><span class="label">HCV</span><span class="value">${p.hcv}</span></div>` : ''}
          ${p.rbs ? `<div class="field"><span class="label">RBS</span><span class="value">${p.rbs}</span></div>` : ''}
          ${p.pt ? `<div class="field"><span class="label">PT</span><span class="value">${p.pt}</span></div>` : ''}
          ${p.aptt ? `<div class="field"><span class="label">APTT</span><span class="value">${p.aptt}</span></div>` : ''}
          ${p.sUricAcid ? `<div class="field"><span class="label">S. Uric Acid</span><span class="value">${p.sUricAcid}</span></div>` : ''}
          ${p.serumBilirubin ? `<div class="field"><span class="label">Serum Bilirubin</span><span class="value">${p.serumBilirubin}</span></div>` : ''}
          ${p.alt ? `<div class="field"><span class="label">ALT</span><span class="value">${p.alt}</span></div>` : ''}
          ${p.sUrea ? `<div class="field"><span class="label">S. Urea</span><span class="value">${p.sUrea}</span></div>` : ''}
          ${p.sCreatinine ? `<div class="field"><span class="label">S. Creatinine</span><span class="value">${p.sCreatinine}</span></div>` : ''}
          ${p.urineProtein24hr ? `<div class="field"><span class="label">24hr Urine Protein</span><span class="value">${p.urineProtein24hr}</span></div>` : ''}
          ${p.ultrasound ? `<div class="field"><span class="label">Ultrasound</span><span class="value">${p.ultrasound}</span></div>` : ''}
          ${p.urineRE ? `<div class="field"><span class="label">Urine R/E</span><span class="value">${p.urineRE}</span></div>` : ''}
          ${p.echo ? `<div class="field"><span class="label">ECHO</span><span class="value">${p.echo}</span></div>` : ''}
        </div>
      </div>
      ` : ''}

      ${(p.operationName || p.operativeFindings || p.anaesthesiaType) ? `
      <div class="section">
        <div class="section-title">Operation & Anaesthesia</div>
        <div class="section-body grid2">
          ${p.operationName ? `<div class="field"><span class="label">Operation Name</span><span class="value">${p.operationName}</span></div>` : ''}
          ${p.operativeFindings ? `<div class="field"><span class="label">Operative Findings</span><span class="value">${p.operativeFindings}</span></div>` : ''}
          ${p.operationComplication ? `<div class="field"><span class="label">Complications</span><span class="value">${p.operationComplication}</span></div>` : ''}
          ${p.biopsy ? `<div class="field"><span class="label">Biopsy</span><span class="value">${p.biopsy}</span></div>` : ''}
          ${p.anaesthesiaType ? `<div class="field"><span class="label">Anaesthesia Type</span><span class="value">${Array.isArray(p.anaesthesiaType) ? p.anaesthesiaType.join(', ') : p.anaesthesiaType}</span></div>` : ''}
${p.anaesthetist ? `<div class="field"><span class="label">Anaesthetist</span><span class="value">${p.anaesthetist}</span></div>` : ''}
          ${p.anaesthesiaComplications ? `<div class="field"><span class="label">Anaesthesia Complications</span><span class="value">${p.anaesthesiaComplications}</span></div>` : ''}
          ${p.anaesthesiaNotes ? `<div class="field"><span class="label">Anaesthesia Notes</span><span class="value">${p.anaesthesiaNotes}</span></div>` : ''}
          ${p.recoveryNotes ? `<div class="field"><span class="label">Recovery Notes</span><span class="value">${p.recoveryNotes}</span></div>` : ''}          ${p.postOpInstructions ? `<div class="field"><span class="label">Post-Op Instructions</span><span class="value">${p.postOpInstructions}</span></div>` : ''}
        </div>
      </div>
      ` : ''}

      ${(p.babyGender || p.babyApgar || p.babyDeliveryWeight) ? `
      <div class="section">
        <div class="section-title">Baby Details</div>
        <div class="section-body grid4">
          ${p.babyGender ? `<div class="field"><span class="label">Baby Gender</span><span class="value">${p.babyGender}</span></div>` : ''}
          ${p.babyApgar ? `<div class="field"><span class="label">Baby APGAR</span><span class="value">${p.babyApgar}</span></div>` : ''}
          ${p.babyDeliveryWeight ? `<div class="field"><span class="label">Baby Weight</span><span class="value">${p.babyDeliveryWeight} kg</span></div>` : ''}
          ${p.babyCry ? `<div class="field"><span class="label">Cry</span><span class="value">${p.babyCry}</span></div>` : ''}
        </div>
      </div>
      ` : ''}

      <div class="signatures">
        <div class="sig-box"><div class="sig-line"></div><div>Nurse</div></div>
        <div class="sig-box"><div class="sig-line"></div><div>Doctor / ${DOCTOR_INFO.name}</div></div>
      </div>

      <script>window.onload = function() { window.print(); }</script>
    </body>
    </html>
  `);
  printWindow.document.close();
};
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <h2 className="text-xl font-bold text-gray-900">Patient Management</h2>
      <div className="flex items-center gap-2">
          {apiLoading && <span className="text-xs text-blue-500 animate-pulse">🔄 Loading...</span>}
          {apiError && <span className="text-xs text-red-500">⚠️ API Error</span>}
          {!apiLoading && !apiError && apiPatients.length > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-green-600">✅ Live ({apiAdmitted.length} admitted)</span>              <button onClick={refetch} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">🔄</button>
            </div>
          )}
<input type="text" placeholder="Search..." value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} className="input-field max-w-xs" />          <button onClick={() => setShowForm(true)} className="btn-primary text-sm">+ Admit Patient</button>
        </div>
      </div>

      {showForm && (
        <div className="card p-6">
          <h3 className="font-bold text-gray-900 mb-4">{editingId ? 'Edit' : 'Admit'} Patient</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div><label className="block text-sm font-medium mb-1">Patient Name *</label><input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="input-field" required /></div>
              <div><label className="block text-sm font-medium mb-1">Husband's Name</label><input type="text" value={form.husbandName} onChange={(e) => setForm({...form, husbandName: e.target.value})} className="input-field" /></div>
              <div><label className="block text-sm font-medium mb-1">Father's Name</label><input type="text" value={form.fatherName} onChange={(e) => setForm({...form, fatherName: e.target.value})} className="input-field" /></div>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
            <div><label className="block text-sm font-medium mb-1">CNIC *</label><input type="text" value={form.cnic} onChange={(e) => setForm({...form, cnic: formatCNIC(e.target.value)})} className="input-field" placeholder="00000-0000000-0" maxLength={15} required /></div>

              <div><label className="block text-sm font-medium mb-1">Age *</label><input type="text" value={form.age} onChange={(e) => setForm({...form, age: e.target.value})} className="input-field" required /></div>
              <div><label className="block text-sm font-medium mb-1">Blood Group</label><select value={form.bloodGroup} onChange={(e) => setForm({...form, bloodGroup: e.target.value})} className="input-field"><option value="">Select</option>{bloodGroups.map(bg => <option key={bg} value={bg}>{bg}</option>)}</select></div>
              <div><label className="block text-sm font-medium mb-1">Phone *</label><input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="input-field" required /></div>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              <div><label className="block text-sm font-medium mb-1">Room</label><input type="text" value={form.room} onChange={(e) => setForm({...form, room: e.target.value})} className="input-field" /></div>
              <div><label className="block text-sm font-medium mb-1">Weight (kg)</label><input type="text" value={form.weight} onChange={(e) => setForm({...form, weight: e.target.value})} className="input-field" /></div>
              <div><label className="block text-sm font-medium mb-1">Emergency Contact</label><input type="text" value={form.emergencyContact} onChange={(e) => setForm({...form, emergencyContact: e.target.value})} className="input-field" placeholder="Name" /></div>
              <div><label className="block text-sm font-medium mb-1">Emergency Phone</label><input type="tel" value={form.emergencyPhone} onChange={(e) => setForm({...form, emergencyPhone: e.target.value})} className="input-field" /></div>
            </div>
            <div><label className="block text-sm font-medium mb-1">Address</label><input type="text" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="input-field" /></div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Allergies</label><input type="text" value={form.allergies} onChange={(e) => setForm({...form, allergies: e.target.value})} className="input-field" placeholder="Known allergies (if any)" /></div>
              <div><label className="block text-sm font-medium mb-1">Referral Source</label><input type="text" value={form.referralSource} onChange={(e) => setForm({...form, referralSource: e.target.value})} className="input-field" placeholder="Self / Referred by Dr..." /></div>
            </div>
            <div><label className="block text-sm font-medium mb-1">Previous Medical History/Surgical History</label><textarea value={form.previousHistory} onChange={(e) => setForm({...form, previousHistory: e.target.value})} className="input-field" rows="2" placeholder="Any previous medical conditions, surgeries, medications..." /></div>
            <div className="grid md:grid-cols-4 gap-4">
              <div><label className="block text-sm font-medium mb-1">Diagnosis</label><input type="text" value={form.diseases} onChange={(e) => setForm({...form, diseases: e.target.value})} className="input-field" /></div>
              <div><label className="block text-sm font-medium mb-1">Indication</label><input type="text" value={form.indication} onChange={(e) => setForm({...form, indication: e.target.value})} className="input-field" /></div>
              <div><label className="block text-sm font-medium mb-1">POG</label><input type="text" value={form.pog} onChange={(e) => setForm({...form, pog: e.target.value})} className="input-field" placeholder="e.g., 38 weeks" /></div>
              <div><label className="block text-sm font-medium mb-1">A/S (APGAR Score)</label><input type="text" value={form.aspiScore} onChange={(e) => setForm({...form, aspiScore: e.target.value})} className="input-field" placeholder="e.g., 9/10" /></div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div><label className="block text-sm font-medium mb-1">Procedure</label><select value={form.procedure} onChange={(e) => setForm({...form, procedure: e.target.value})} className="input-field"><option value="">Select</option>{procedures.map(p => <option key={p} value={p}>{p}</option>)}</select></div>
              <div><label className="block text-sm font-medium mb-1">Doctor</label><input type="text" value={form.doctorName} onChange={(e) => setForm({...form, doctorName: e.target.value})} className="input-field" /></div>
              <div><label className="block text-sm font-medium mb-1">Staff</label><input type="text" value={form.staffName} onChange={(e) => setForm({...form, staffName: e.target.value})} className="input-field" /></div>
            </div>
            <div><label className="block text-sm font-medium mb-1">Notes</label><textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="input-field" rows="2" /></div>
            <div>
              {/* ── LMP & Discharge ── */}
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">LMP</label><input type="date" value={form.lmp} onChange={(e) => setForm({...form, lmp: e.target.value})} className="input-field" /></div>
              <div><label className="block text-sm font-medium mb-1">Date of Discharge (if known)</label><input type="date" value={form.dateOfDischarge} onChange={(e) => setForm({...form, dateOfDischarge: e.target.value})} className="input-field" /></div>
            </div>

            {/* ── HISTORY & EXAMINATION ── */}
            <div className="border border-green-200 rounded-lg p-4 bg-green-50">
              <h4 className="font-bold text-green-800 mb-4 text-sm uppercase tracking-wide">📄 History & Examination</h4>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">History</p>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div><label className="block text-sm font-medium mb-1">Chief Complaints (C/C)</label><textarea value={form.chiefComplaints} onChange={(e) => setForm({...form, chiefComplaints: e.target.value})} className="input-field" rows="2" placeholder="Main complaints..." /></div>
                <div><label className="block text-sm font-medium mb-1">History of Present Illness (H.O.P.I)</label><textarea value={form.historyOfPresentIllness} onChange={(e) => setForm({...form, historyOfPresentIllness: e.target.value})} className="input-field" rows="2" placeholder="Detailed history..." /></div>
                <div><label className="block text-sm font-medium mb-1">Past History</label><textarea value={form.pastHistory} onChange={(e) => setForm({...form, pastHistory: e.target.value})} className="input-field" rows="2" placeholder="Previous conditions, surgeries..." /></div>
                <div><label className="block text-sm font-medium mb-1">Obs. History (G / P / A / A)</label><textarea value={form.obsHistory} onChange={(e) => setForm({...form, obsHistory: e.target.value})} className="input-field" rows="2" placeholder="Gravida / Para / Abortion / Alive" /></div>
                <div><label className="block text-sm font-medium mb-1">Gynaecological History – Cycle</label><input type="text" value={form.gynaecologicalHistory} onChange={(e) => setForm({...form, gynaecologicalHistory: e.target.value})} className="input-field" placeholder="e.g. Regular 28-day cycle" /></div>
                <div><label className="block text-sm font-medium mb-1">Allergies & Background Meds</label><input type="text" value={form.allergiesBackground} onChange={(e) => setForm({...form, allergiesBackground: e.target.value})} className="input-field" placeholder="Known allergies, long-term medications..." /></div>
              </div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">General Examination</p>
              <div className="grid md:grid-cols-4 gap-4 mb-4">
                <div><label className="block text-sm font-medium mb-1">Anaemia</label><input type="text" value={form.anaemia} onChange={(e) => setForm({...form, anaemia: e.target.value})} className="input-field" placeholder="+/-" /></div>
                <div><label className="block text-sm font-medium mb-1">Jaundice</label><input type="text" value={form.jaundice} onChange={(e) => setForm({...form, jaundice: e.target.value})} className="input-field" placeholder="+/-" /></div>
                <div><label className="block text-sm font-medium mb-1">Clubbing</label><input type="text" value={form.clubbing} onChange={(e) => setForm({...form, clubbing: e.target.value})} className="input-field" placeholder="+/-" /></div>
                <div><label className="block text-sm font-medium mb-1">Cyanosis</label><input type="text" value={form.cyanosis} onChange={(e) => setForm({...form, cyanosis: e.target.value})} className="input-field" placeholder="+/-" /></div>
                <div><label className="block text-sm font-medium mb-1">Dehydration</label><input type="text" value={form.dehydration} onChange={(e) => setForm({...form, dehydration: e.target.value})} className="input-field" placeholder="+/-" /></div>
                <div><label className="block text-sm font-medium mb-1">B/P (mmHg)</label><input type="text" value={form.bp} onChange={(e) => setForm({...form, bp: e.target.value})} className="input-field" placeholder="120/80" /></div>
                <div><label className="block text-sm font-medium mb-1">Pulse (bpm)</label><input type="text" value={form.pulse} onChange={(e) => setForm({...form, pulse: e.target.value})} className="input-field" placeholder="80" /></div>
                <div><label className="block text-sm font-medium mb-1">Temp / Edema / Resp (TEP)</label><input type="text" value={form.tempEdemaResp} onChange={(e) => setForm({...form, tempEdemaResp: e.target.value})} className="input-field" placeholder="e.g. Afebrile, No edema" /></div>
              </div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Systemic Examination</p>
              <div className="grid md:grid-cols-4 gap-4 mb-4">
                <div><label className="block text-sm font-medium mb-1">CVS</label><textarea value={form.cvs} onChange={(e) => setForm({...form, cvs: e.target.value})} className="input-field" rows="2" placeholder="Cardiovascular..." /></div>
                <div><label className="block text-sm font-medium mb-1">CNS</label><textarea value={form.cns} onChange={(e) => setForm({...form, cns: e.target.value})} className="input-field" rows="2" placeholder="Neurological..." /></div>
                <div><label className="block text-sm font-medium mb-1">Resp.</label><textarea value={form.resp} onChange={(e) => setForm({...form, resp: e.target.value})} className="input-field" rows="2" placeholder="Respiratory..." /></div>
                <div><label className="block text-sm font-medium mb-1">GIT</label><textarea value={form.git} onChange={(e) => setForm({...form, git: e.target.value})} className="input-field" rows="2" placeholder="Gastrointestinal..." /></div>
              </div>
              <div><label className="block text-sm font-medium mb-1">Provisional Diagnosis & Notes</label><textarea value={form.provisionalDiagnosis} onChange={(e) => setForm({...form, provisionalDiagnosis: e.target.value})} className="input-field" rows="2" placeholder="Provisional diagnosis and any additional clinical observations..." /></div>
            </div>

            {/* ── INVESTIGATIONS ── */}
            <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
              <h4 className="font-bold text-yellow-800 mb-4 text-sm uppercase tracking-wide">🔬 Investigations</h4>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Haematology & Serology</p>
              <div className="grid md:grid-cols-4 gap-4 mb-4">
                <div><label className="block text-sm font-medium mb-1">CBC / Hb%</label><input type="text" value={form.cbcHb} onChange={(e) => setForm({...form, cbcHb: e.target.value})} className="input-field" placeholder="g/dL" /></div>
                <div><label className="block text-sm font-medium mb-1">Platelet Count</label><input type="text" value={form.plateletCount} onChange={(e) => setForm({...form, plateletCount: e.target.value})} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">HbsAg</label><input type="text" value={form.hbsAg} onChange={(e) => setForm({...form, hbsAg: e.target.value})} className="input-field" placeholder="+/-" /></div>
                <div><label className="block text-sm font-medium mb-1">HCV</label><input type="text" value={form.hcv} onChange={(e) => setForm({...form, hcv: e.target.value})} className="input-field" placeholder="+/-" /></div>
                <div><label className="block text-sm font-medium mb-1">RBS (mg/dL)</label><input type="text" value={form.rbs} onChange={(e) => setForm({...form, rbs: e.target.value})} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">PT (sec)</label><input type="text" value={form.pt} onChange={(e) => setForm({...form, pt: e.target.value})} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">APTT (sec)</label><input type="text" value={form.aptt} onChange={(e) => setForm({...form, aptt: e.target.value})} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">S. Uric Acid</label><input type="text" value={form.sUricAcid} onChange={(e) => setForm({...form, sUricAcid: e.target.value})} className="input-field" /></div>
              </div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">LFTs & Renal</p>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div><label className="block text-sm font-medium mb-1">Serum Bilirubin</label><input type="text" value={form.serumBilirubin} onChange={(e) => setForm({...form, serumBilirubin: e.target.value})} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">Alkaline Phosphatase</label><input type="text" value={form.alkalinePhosphatase} onChange={(e) => setForm({...form, alkalinePhosphatase: e.target.value})} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">ALT</label><input type="text" value={form.alt} onChange={(e) => setForm({...form, alt: e.target.value})} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">S. Urea</label><input type="text" value={form.sUrea} onChange={(e) => setForm({...form, sUrea: e.target.value})} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">S. Creatinine</label><input type="text" value={form.sCreatinine} onChange={(e) => setForm({...form, sCreatinine: e.target.value})} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">24-Hr Urine Protein</label><input type="text" value={form.urineProtein24hr} onChange={(e) => setForm({...form, urineProtein24hr: e.target.value})} className="input-field" placeholder="mg/24hr" /></div>
              </div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Imaging & Other</p>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div><label className="block text-sm font-medium mb-1">Ultrasound</label><textarea value={form.ultrasound} onChange={(e) => setForm({...form, ultrasound: e.target.value})} className="input-field" rows="2" placeholder="Findings..." /></div>
                <div><label className="block text-sm font-medium mb-1">Urine R/E</label><textarea value={form.urineRE} onChange={(e) => setForm({...form, urineRE: e.target.value})} className="input-field" rows="2" placeholder="Findings..." /></div>
              </div>
              <div><label className="block text-sm font-medium mb-1">E.C.H.O</label><input type="text" value={form.echo} onChange={(e) => setForm({...form, echo: e.target.value})} className="input-field" placeholder="Echocardiography result" /></div>
            </div>

            {/* ── OPERATION & ANAESTHESIA ── */}
            <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
              <h4 className="font-bold text-purple-800 mb-4 text-sm uppercase tracking-wide">✂️ Operation & Anaesthesia</h4>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Operation</p>
              <div className="space-y-4 mb-4">
                <div><label className="block text-sm font-medium mb-1">Operation Name</label><input type="text" value={form.operationName} onChange={(e) => setForm({...form, operationName: e.target.value})} className="input-field" placeholder="e.g. Lower Segment Caesarean Section" /></div>
                <div><label className="block text-sm font-medium mb-1">Operative Findings/Procedure </label><textarea value={form.operativeFindings} onChange={(e) => setForm({...form, operativeFindings: e.target.value})} className="input-field" rows="2" placeholder="Intra-operative findings and procedure notes..." /></div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Complication (if any)</label><input type="text" value={form.operationComplication} onChange={(e) => setForm({...form, operationComplication: e.target.value})} className="input-field" placeholder="Intra-operative complications..." /></div>
                  <div><label className="block text-sm font-medium mb-1">Biopsy</label><input type="text" value={form.biopsy} onChange={(e) => setForm({...form, biopsy: e.target.value})} className="input-field" placeholder="Biopsy details if any" /></div>
                </div>
              </div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Anaesthesia</p>
              <div className="space-y-4 mb-4">
                <div className="flex gap-6">
                  {['GA (General)', 'Spinal', 'Local / Regional'].map(type => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.anaesthesiaType.includes(type)}
                        onChange={(e) => {
                          const updated = e.target.checked
                            ? [...form.anaesthesiaType, type]
                            : form.anaesthesiaType.filter(t => t !== type);
                          setForm({...form, anaesthesiaType: updated});
                        }} className="w-4 h-4" />
                      <span className="text-sm">{type}</span>
                    </label>
                  ))}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Anaesthetist</label><input type="text" value={form.anaesthetist} onChange={(e) => setForm({...form, anaesthetist: e.target.value})} className="input-field" placeholder="Name" /></div>
                  <div><label className="block text-sm font-medium mb-1">Anaesthesia Complications</label><input type="text" value={form.anaesthesiaComplications} onChange={(e) => setForm({...form, anaesthesiaComplications: e.target.value})} className="input-field" placeholder="Any complications..." /></div>
                </div>
                <div><label className="block text-sm font-medium mb-1">Anaesthesia Notes</label><textarea value={form.anaesthesiaNotes} onChange={(e) => setForm({...form, anaesthesiaNotes: e.target.value})} className="input-field" rows="2" placeholder="e.g. Spinal at L3-L4, Bupivacaine 10mg..." /></div>
                <div><label className="block text-sm font-medium mb-1">Recovery & Post-Op Notes</label><textarea value={form.recoveryNotes} onChange={(e) => setForm({...form, recoveryNotes: e.target.value})} className="input-field" rows="2" placeholder="Post-anaesthesia recovery, patient condition..." /></div>
                <div><label className="block text-sm font-medium mb-1">Post-Op Instructions</label><textarea value={form.postOpInstructions} onChange={(e) => setForm({...form, postOpInstructions: e.target.value})} className="input-field" rows="2" placeholder="Care instructions, medications, follow-up date..." /></div>
              </div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Baby Details (If Delivery)</p>
              <div className="grid md:grid-cols-4 gap-4">
                <div><label className="block text-sm font-medium mb-1">Baby Gender</label>
                  <select value={form.babyGender} onChange={(e) => setForm({...form, babyGender: e.target.value})} className="input-field">
                    <option value="">—</option><option value="Male">Male</option><option value="Female">Female</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium mb-1">Baby APGAR</label><input type="text" value={form.babyApgar} onChange={(e) => setForm({...form, babyApgar: e.target.value})} className="input-field" placeholder="e.g. 9/10" /></div>
                <div><label className="block text-sm font-medium mb-1">Baby Weight (kg)</label><input type="text" value={form.babyDeliveryWeight} onChange={(e) => setForm({...form, babyDeliveryWeight: e.target.value})} className="input-field" placeholder="e.g. 3.2" /></div>
                <div><label className="block text-sm font-medium mb-1">Cry</label>
                  <select value={form.babyCry} onChange={(e) => setForm({...form, babyCry: e.target.value})} className="input-field">
                    <option value="">—</option><option value="Immediate">Immediate</option><option value="Delayed">Delayed</option><option value="No Cry">No Cry</option>
                  </select>
                </div>
              </div>
            </div>
  <label className="block text-sm font-medium mb-1">Admit Date & Time *</label>
  <input 
    type="datetime-local" 
    value={form.admitOn} 
    onChange={(e) => setForm({...form, admitOn: e.target.value})} 
    className="input-field" 
    required 
  />
</div>
            {/* PHC General Consent */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.generalConsent} onChange={(e) => setForm({...form, generalConsent: e.target.checked})} className="mt-1 w-5 h-5" required />
                <div>
                  <span className="font-medium text-yellow-800">General Consent for Treatment (PHC Required) *</span>
                  <p className="text-sm text-yellow-700 mt-1">I hereby consent to admission and treatment at Dr. Ruqqia Sultana Clinic & Maternity Home. I authorize the medical staff to perform necessary examinations, tests, and treatments. I understand the risks and benefits have been explained to me.</p>
                </div>
              </label>
            </div>

            <div className="flex gap-2">
<button type="submit" disabled={patientSubmitting} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60">
  {patientSubmitting ? (
    <><span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span> Saving...</>
  ) : (editingId ? 'Update' : 'Admit')}
</button>              <button type="button" onClick={resetForm} className="btn-secondary text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="card p-4">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-green-500"></span>Admitted ({admitted.length})</h3>
{apiLoading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <span className="animate-spin inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></span>
            <p className="text-gray-500 text-sm">Loading patients...</p>
          </div>
        ) :
         admitted.length > 0 ? (       
             <>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr><th>MR No</th><th>Name</th><th>Room</th><th>Admission</th><th>Diagnosis</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {paginatedAdmitted.map(p => {
                  const dues = getPatientDues(p.id);
                  return (
                    <tr key={p.id} className={p.dischargeRequested ? 'bg-yellow-50' : ''}>
                      <td className="font-mono text-sm">{p.mrNo}</td>
                      <td className="font-medium">{p.name}</td>
                      <td>{p.room || '-'}</td>
                      <td className="text-sm">{p.admissionDate}<br/><span className="text-gray-400">{p.admissionTime}</span></td>
                      <td>{p.diseases || '-'}</td>
                      <td>
                        {p.dischargeRequested ? (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">⏳ Discharge Pending</span>
                        ) : (
                        <span className={`px-2 py-1 rounded text-xs ${
  p.status?.toLowerCase() === 'reject' || p.status?.toLowerCase() === 'rejected'
    ? 'bg-red-100 text-red-700'
    : 'bg-green-100 text-green-700'
}`}>
  🏥 {p.status || 'Admitted'}
</span>
                        )}
                      </td>
                    <td>
  <div className="flex gap-1 flex-wrap">
    <button onClick={() => handleEdit(p)} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">Edit</button>
    <button onClick={() => printPatientDetail(p)} className="text-xs px-2 py-1 bg-purple-100 text-purple-600 rounded hover:bg-purple-200">🖨 Print</button>
    {p.dischargeRequested ? (
      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded">Awaiting Admin</span>
    ) : (
      <button onClick={() => handleDischargeClick(p)} className="text-xs px-2 py-1 bg-orange-100 text-orange-600 rounded hover:bg-orange-200">
        Request Discharge
      </button>
    )}
  </div>
</td>
                    </tr>
                  );
                })}
             </tbody>
              </table>
            </div>
            {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 bg-white border-t rounded-b-lg">
              <p className="text-sm text-gray-500">
                Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, admitted.length)} of {admitted.length} patients
              </p>
              <div className="flex items-center gap-1">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-2 py-1 rounded text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-40">⏮</button>
                <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className="px-2 py-1 rounded text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-40">◀</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === '...'
                      ? <span key={`e-${idx}`} className="px-2 text-gray-400">…</span>
                      : <button key={p} onClick={() => setCurrentPage(p)}
                          className={`px-3 py-1 rounded text-sm font-medium ${currentPage === p ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                        >{p}</button>
                  )
                }
                <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className="px-2 py-1 rounded text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-40">▶</button>
               <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-2 py-1 rounded text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-40">⏭</button>
              </div>
            </div>
          )}
          </>
      ) : (
  <p className="text-gray-500 text-center py-8">No admitted patients</p>
        ) }
      </div>

      <div className="card p-4">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-gray-400"></span>Recently Discharged</h3>
        {discharged.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>MR No</th><th>Name</th><th>Admission</th><th>Discharge</th><th>Procedure</th></tr></thead>
              <tbody>
                {discharged.map(p => (
                  <tr key={p.id} className="opacity-70">
                    <td className="font-mono text-sm">{p.mrNo}</td>
                    <td>{p.name}</td>
                    <td>{p.admissionDate}</td>
                    <td>{p.dischargeDate}</td>
                    <td>{p.procedure || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-gray-500 text-center py-4">No discharged patients</p>}
      </div>

      {/* Discharge Confirmation Modal */}
      {dischargeModal && (
        <div className="modal-overlay" onClick={() => setDischargeModal(null)}>
          <div className="modal-content p-6 max-w-lg" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Discharge Patient</h3>
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <p><strong>Patient:</strong> {dischargeModal.name}</p>
              <p><strong>MR No:</strong> {dischargeModal.mrNo}</p>
              <p><strong>Admission:</strong> {dischargeModal.admissionDate}</p>
            </div>
            
            {/* Discharge Conditions */}
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Patient Condition at Discharge *</label>
                <select value={dischargeCondition.patientCondition} onChange={(e) => setDischargeCondition({...dischargeCondition, patientCondition: e.target.value})} className="input-field" required>
                  <option value="">Select Condition</option>
                  <option value="Stable">Stable</option>
                  <option value="Good">Good</option>
                  <option value="Satisfactory">Satisfactory</option>
                  <option value="Fair">Fair</option>
                  <option value="Critical">Critical</option>
                  <option value="Referred">Referred to Higher Center</option>
                  <option value="LAMA">Left Against Medical Advice (LAMA)</option>
                </select>
              </div>
              {getPatientBaby(dischargeModal.id) && (
                <div>
                  <label className="block text-sm font-medium mb-1">Baby Condition at Discharge</label>
                  <select value={dischargeCondition.babyCondition} onChange={(e) => setDischargeCondition({...dischargeCondition, babyCondition: e.target.value})} className="input-field">
                    <option value="">Select Condition</option>
                    <option value="Healthy">Healthy</option>
                    <option value="Stable">Stable</option>
                    <option value="Under Observation">Under Observation</option>
                    <option value="NICU Admitted">NICU Admitted</option>
                    <option value="Referred">Referred</option>
                  </select>
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-blue-800 text-sm">
                <strong>Note:</strong> This will send a discharge request to Admin. Admin will review the patient's records and billing before approving the discharge.
              </p>
            </div>
            <div className="flex gap-2">
              <button 
  onClick={confirmDischarge} 
  disabled={dischargeSubmitting}
  className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
>
  {dischargeSubmitting ? (
    <><span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span> Sending...</>
  ) : '📤 Request Discharge Approval'}
</button>
              <button onClick={() => setDischargeModal(null)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const PatientSearchDropdown = ({ patients, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = React.useRef(null);

  const selected = patients.find(p => String(p.id) === String(value));
  const filtered = patients.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    (p.mrNo && p.mrNo.toLowerCase().includes(search.toLowerCase()))
  );

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => { setOpen(!open); setSearch(''); }}
        className="input-field w-full text-left flex justify-between items-center"
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
          {selected ? `${selected.name}${selected.mrNo ? ` (${selected.mrNo})` : ''}` : 'Select Patient'}
        </span>
        <span className="text-gray-400">▾</span>
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 z-50 bg-white border border-gray-200 rounded-lg shadow-xl mt-1 max-h-60 flex flex-col">
          <div className="p-2 border-b">
            <input
              type="text"
              autoFocus
              placeholder="Search patient..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field text-sm w-full"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            <div
              className="px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 cursor-pointer"
              onClick={() => { onChange(''); setOpen(false); }}
            >
              Select Patient
            </div>
            {filtered.map(p => (
              <div
                key={p.id}
                onClick={() => { onChange(String(p.id)); setOpen(false); }}
                className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 hover:text-blue-700 ${String(p.id) === String(value) ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-900'}`}
              >
                {p.name}{p.mrNo ? ` (${p.mrNo})` : ''}
              </div>
            ))}
            {filtered.length === 0 && <div className="px-3 py-4 text-sm text-gray-400 text-center">No patients found</div>}
          </div>
        </div>
      )}
    </div>
  );
};
const MedicalRecordsTab = () => {
  const { patients, medicalRecords, addMedicalRecord } = useData();
  const [selectedPatient, setSelectedPatient] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null); // holds API record being edited
  const [apiRecords, setApiRecords] = useState([]);
  const [apiLoading, setApiLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
const [form, setForm] = useState({ patientId: '', type: 'medication', description: '', medication: '', dosage: '', operationName: '', operationNotes: '', deliveryType: '', babyWeight: '', babyGender: '', complications: '' });
  const [dropdownPatients, setDropdownPatients] = useState([]);

  // Fetch patient dropdown from API
  useEffect(() => {
    getPatientDropdown()
      .then(data => setDropdownPatients(Array.isArray(data) ? data : []))
      .catch(() => setDropdownPatients([]));
  }, []);

  // Fetch all medical records on mount
  const fetchApiRecords = useCallback(async (pid = 0) => {
    setApiLoading(true);
    setApiError(null);
    try {
      const data = await getMedicalRecords(pid);
      setApiRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setApiLoading(false);
    }
  }, []);

  useEffect(() => { fetchApiRecords(0); }, [fetchApiRecords]);

  // Re-fetch when patient filter changes
  useEffect(() => {
    fetchApiRecords(selectedPatient ? Number(selectedPatient) : 0);
  }, [selectedPatient, fetchApiRecords]);

  const admittedPatients = patients.filter(p => p.status === 'admitted');
  const recordTypes = [
    { id: 'medication', label: '💊 Medication Given' },
    { id: 'procedure', label: '🔧 Procedure/Treatment' },
    { id: 'operation', label: '🏥 Operation' },
    { id: 'delivery', label: '👶 Delivery' },
    { id: 'note', label: '📝 General Note' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const patient = patients.find(p => String(p.id) === String(form.patientId));

    const apiPayload = {
      id: editingRecord ? editingRecord.id : 0,
      patientId: Number(form.patientId) || 0,
      recordType: form.type || '',
      medication: form.medication || '',
      dosage: form.dosage || '',
      descriptionNotes: form.description || '',
      complications: form.complications || '',
      createdDate: new Date().toISOString(),
    };

    try {
      if (editingRecord) {
        await updateMedicalRecord(apiPayload);
        console.log('Medical record updated in API');
      } else {
        await createMedicalRecord(apiPayload);
        console.log('Medical record saved to API');
      }
      // Refresh from API
      await fetchApiRecords(selectedPatient ? Number(selectedPatient) : 0);
 } catch (err) {
      alert('Failed to save medical record: ' + err.message);
      return;
    }

    setForm({ patientId: '', type: 'medication', description: '', medication: '', dosage: '', operationName: '', operationNotes: '', deliveryType: '', babyWeight: '', babyGender: '', complications: '' });
    setEditingRecord(null);
    setShowForm(false);
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setForm({
      patientId: String(record.patientId),
      type: record.recordType || 'medication',
      description: record.descriptionNotes || '',
      medication: record.medication || '',
      dosage: record.dosage || '',
      operationName: '',
      operationNotes: '',
      deliveryType: '',
      babyWeight: '',
      babyGender: '',
      complications: record.complications || '',
    });
    setShowForm(true);
  };

  // Use API records if available, fallback to local
  const patientRecords = apiRecords.length > 0 ? apiRecords : (selectedPatient ? medicalRecords.filter(r => r.patientId === selectedPatient) : medicalRecords);
  return (
    <div className="space-y-6">
     <div className="flex flex-wrap justify-between items-center gap-4">
        <h2 className="text-xl font-bold text-gray-900">Medical Records</h2>
        <div className="flex items-center gap-2">
          {apiLoading && <span className="text-xs text-blue-500 animate-pulse">🔄 Loading...</span>}
          {apiError && <span className="text-xs text-red-500">⚠️ API Error</span>}
          {!apiLoading && !apiError && apiRecords.length > 0 && (
            <span className="text-xs text-green-600">✅ Live ({apiRecords.length} records)</span>
          )}
          <button onClick={() => fetchApiRecords(selectedPatient ? Number(selectedPatient) : 0)} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">🔄</button>
<select value={selectedPatient} onChange={(e) => setSelectedPatient(e.target.value)} className="input-field">
            <option value="">All Patients</option>
            {dropdownPatients.length > 0
              ? dropdownPatients.map(p => <option key={p.id} value={p.id}>{p.name}{p.mrNo ? ` (${p.mrNo})` : ''}</option>)
              : admittedPatients.map(p => <option key={p.id} value={p.id}>{p.name} ({p.mrNo})</option>)
            }
          </select>
          <button onClick={() => { setEditingRecord(null); setForm({ patientId: '', type: 'medication', description: '', medication: '', dosage: '', operationName: '', operationNotes: '', deliveryType: '', babyWeight: '', babyGender: '', complications: '' }); setShowForm(true); }} className="btn-primary text-sm">+ Add Record</button>
        </div>
      </div>

      {showForm && (
        <div className="card p-6">
          <h3 className="font-bold text-gray-900 mb-4">{editingRecord ? 'Edit Medical Record' : 'Add Medical Record'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
            <div className="relative">
                <label className="block text-sm font-medium mb-1">Patient *</label>
                <PatientSearchDropdown
                  patients={dropdownPatients.length > 0 ? dropdownPatients : admittedPatients}
                  value={form.patientId}
                  onChange={(id) => setForm({...form, patientId: id})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Record Type *</label>
                <select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="input-field">
                  {recordTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
            </div>

            {form.type === 'medication' && (
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Medication</label><input type="text" value={form.medication} onChange={(e) => setForm({...form, medication: e.target.value})} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">Dosage</label><input type="text" value={form.dosage} onChange={(e) => setForm({...form, dosage: e.target.value})} className="input-field" /></div>
              </div>
            )}

            {form.type === 'operation' && (
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Operation Name</label><input type="text" value={form.operationName} onChange={(e) => setForm({...form, operationName: e.target.value})} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">Operation Notes</label><input type="text" value={form.operationNotes} onChange={(e) => setForm({...form, operationNotes: e.target.value})} className="input-field" /></div>
              </div>
            )}

            {form.type === 'delivery' && (
              <div className="grid md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium mb-1">Delivery Type</label><select value={form.deliveryType} onChange={(e) => setForm({...form, deliveryType: e.target.value})} className="input-field"><option value="">Select</option><option value="NVD">NVD</option><option value="LSCS">LSCS (C-Section)</option><option value="Assisted">Assisted</option></select></div>
                <div><label className="block text-sm font-medium mb-1">Baby Weight (kg)</label><input type="text" value={form.babyWeight} onChange={(e) => setForm({...form, babyWeight: e.target.value})} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">Baby Gender</label><select value={form.babyGender} onChange={(e) => setForm({...form, babyGender: e.target.value})} className="input-field"><option value="">Select</option><option value="Male">Male</option><option value="Female">Female</option></select></div>
              </div>
            )}

            <div><label className="block text-sm font-medium mb-1">Description/Notes</label><textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="input-field" rows="3" /></div>
            <div><label className="block text-sm font-medium mb-1">Complications (if any)</label><input type="text" value={form.complications} onChange={(e) => setForm({...form, complications: e.target.value})} className="input-field" /></div>

            <div className="flex gap-2">
              <button type="submit" className="btn-primary text-sm">Save Record</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {patientRecords.length > 0 ? patientRecords.map(record => {
          // Support both API shape and local shape
          const isApi = record.recordType !== undefined;
          const patient = patients.find(p => p.id === record.patientId);
          const displayType = isApi ? record.recordType : record.type;
          const displayDesc = isApi ? record.descriptionNotes : record.description;
          const displayDate = isApi ? new Date(record.createdDate).toLocaleDateString() : record.date;
          const displayTime = isApi ? new Date(record.createdDate).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : record.time;
          const displayPatientName = isApi ? (patient?.name || `Patient #${record.patientId}`) : record.patientName;
          const displayMrNo = isApi ? (patient?.mrNo || '') : record.mrNo;

          return (
            <div key={record.id} className="card p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <span className="text-lg mr-2">{recordTypes.find(t => t.id === displayType)?.label.split(' ')[0] || '📋'}</span>
                  <span className="font-bold text-gray-900">{displayPatientName}</span>
                  {displayMrNo && <span className="text-gray-500 text-sm ml-2">({displayMrNo})</span>}
                  <span className="ml-2 px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">{displayType}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{displayDate} {displayTime}</span>
                  {isApi && (
                    <button onClick={() => handleEditRecord(record)} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200">Edit</button>
                  )}
                </div>
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                {record.medication && record.medication !== 'string' && <p><strong>Medication:</strong> {record.medication} {record.dosage && record.dosage !== 'string' ? `- ${record.dosage}` : ''}</p>}
                {record.operationName && <p><strong>Operation:</strong> {record.operationName}</p>}
                {record.deliveryType && <p><strong>Delivery:</strong> {record.deliveryType} | Baby: {record.babyGender}, {record.babyWeight}kg</p>}
                {displayDesc && displayDesc !== 'string' && <p>{displayDesc}</p>}
                {record.complications && record.complications !== 'string' && <p className="text-red-600"><strong>Complications:</strong> {record.complications}</p>}
              </div>
            </div>
          );
        }) : <div className="text-center py-12 text-gray-500">No medical records found</div>}
      </div>
    </div>
  );
};

const BabyRecordsTab = () => {
  const { patients, birthRecords, babyMedicalRecords, addBirthRecord, updateBirthRecord, addBabyMedicalRecord } = useData();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedBaby, setSelectedBaby] = useState(null);
  const [showMedicalForm, setShowMedicalForm] = useState(false);
  const [showNurseryForm, setShowNurseryForm] = useState(false);
  const [shiftingBaby, setShiftingBaby] = useState(null);
  const [medicalForm, setMedicalForm] = useState({ babyId: '', type: 'observation', description: '', medication: '', dosage: '', notes: '' });
  const [showProformaForm, setShowProformaForm] = useState(false);
  const [viewNurseryModal, setViewNurseryModal] = useState(null); // holds { baby, records }
  const [viewNurseryLoading, setViewNurseryLoading] = useState(false);
  const [viewNurseryError, setViewNurseryError] = useState('');
  const [proformaForm, setProformaForm] = useState({
    babyId: '',
    // Vitals at Admission
    spo2Admission: '',
    heartRateAdmission: '',
    respiratoryRateAdmission: '',
    presentingComplaints: '',
    
    // General Physical Examination
    generalCondition: '',
    pretermRisk: '',
    cranium: '',
    gruntingAssessment: '',
    newbornAssessment: '',
    
    // Home Treatment
    plasma: '',
    micro: '',
    sucking: '',
    neonatalReflexes: '',
    structuralBaby: '',
    
    // Vitals at Discharge
    spo2Discharge: '',
    heartRateDischarge: '',
    
    // Treatment Given (array of treatments)
    treatments: []
  });
  const [form, setForm] = useState({ babyName: '', motherName: '', fatherName: '', sex: 'Male', birthWeight: '', dateOfBirth: new Date().toISOString().split('T')[0], timeOfBirth: '', amPm: 'AM', modeOfDelivery: 'NVD', cellNo: '', nicuStay: '0', address: '', patientId: '', nurseryStatus: 'observation', nurseryAdmitTime: '', apgarScore: '' });
  
  const [nurseryForm, setNurseryForm] = useState({
    // Baby Examined
    modeOfDelivery: '',
    gender: '',
    apgarScore: '',
    immediateCry: '',
    weight: '',
    color: '',
    af: '',
    analOpening: '',
    backSpine: '',
    genitelia: '',
    eyesNoseEar: '',
    cord: '',
    cns: '',
    cvs: '',
    oralCavity: '',
    upperLowerLimbs: '',
    // Risk Factors
    periodOfGestation: '',
    historyOfLeaking: '',
    tetanusVaccination: '',
    historyOfDiabetes: '',
    historyOfHypertension: '',
    historyOfTwins: '',
    historyOfIUGR: '',
    historyOfPreterm: '',
    historyOfIUD: '',
    previousAnomalousBaby: '',
    motherBloodGroup: '',
    hbsAg: '',
    antiHCV: '',
    // Advice
    advice: '',
    injections: 'Vitamin K 10units I/M Stat',
    others: '',
    causeOfReferral: '',
    doctorName: DOCTOR_INFO.name
  });


  const [dropdownPatients, setDropdownPatients] = useState([]);

useEffect(() => {
  getPatientDropdown()
    .then(data => setDropdownPatients(Array.isArray(data) ? data : []))
    .catch(() => setDropdownPatients([]));
}, []);
const { apiBabies, loading: babiesLoading, refetch: refetchBabies } = useApiBabies();

const apiBabyRecords = apiBabies
    .filter(b => true) // show all API records
    .map(b => ({
id: `api-${b.id}-${b.birthTime}`,
_apiId: b.id,
      babyName: b.babyName && b.babyName !== 'string' ? b.babyName : '',
      motherName: b.motherName && b.motherName !== 'string' ? b.motherName : 'Unknown',
      fatherName: b.fatherName && b.fatherName !== 'string' ? b.fatherName : '',
      sex: b.gender && b.gender !== 'string' ? b.gender : 'Unknown',
      birthWeight: b.weightKg || 0,
      dateOfBirth: b.dateOfBirth ? new Date(b.dateOfBirth).toLocaleDateString() : '',
      timeOfBirth: b.birthTime ? new Date(b.birthTime).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : '',
      amPm: b.amPm && b.amPm !== 'st' ? b.amPm : '',
      modeOfDelivery: b.modeOfDelivery && b.modeOfDelivery !== 'string' ? b.modeOfDelivery : '',
      apgarScore: b.apgarScore || '',
      cellNo: b.cellNo && b.cellNo !== 'string' ? b.cellNo : '',
      address: b.address && b.address !== 'string' ? b.address : '',
 certificateNo: `BC-API-${b.motherPatientId}`,
      motherPatientId: b.motherPatientId,
           daycareStatus: b.babyStatus === 'with mother' ? 'with-mother' : 'in-daycare',
      daycareAdmissionTime: b.createdDate || new Date().toISOString(),
      nurseryStatus: b.babyStatus === 'with mother' ? 'discharged' : 'observation',
      _fromApi: true,
    }));

  const allBabyRecords = [
    ...apiBabyRecords,
    ...birthRecords,
  ];
  const admittedPatients = patients.filter(p => p.status === 'admitted');

  // Check if baby needs shift notification (3 hours passed)

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingId) {
      updateBirthRecord(editingId, form);
      resetForm();
      return;
    }
    setBabySubmitting(true);

    // Build baby API payload

    // Build baby API payload
    const dobISO = form.dateOfBirth ? new Date(form.dateOfBirth).toISOString() : new Date().toISOString();
    const birthTimeISO = (() => {
      try {
        if (form.timeOfBirth) {
          const d = new Date(`${form.dateOfBirth}T${form.timeOfBirth}`);
          if (!isNaN(d)) return d.toISOString();
        }
        return new Date().toISOString();
      } catch {
        return new Date().toISOString();
      }
    })();

    const babyApiPayload = {
      id: 0,
      motherPatientId: Number(form.patientId) || 0,
      babyName: form.babyName || '',
      gender: form.sex || 'Male',
apgarScore: (() => {
  const raw = String(form.apgarScore || '').trim();
  // Handle "9/10" format — take the first number before the slash
  const parsed = parseFloat(raw.split('/')[0]);
  return isNaN(parsed) ? 0 : parsed;
})(),      motherName: form.motherName || '',
      fatherName: form.fatherName || '',
      dateOfBirth: dobISO,
      birthTime: birthTimeISO,
      amPm: form.amPm || 'AM',
      weightKg: Number(form.birthWeight) || 0,
      modeOfDelivery: form.modeOfDelivery || 'NVD',
      cellNo: form.cellNo || '',
      address: form.address || '',
      createdDate: new Date().toISOString(),
    };

   try {
      const result = await createBabyRecord(babyApiPayload);
      console.log('Baby saved to API:', result);
    } catch (err) {
      setBabySubmitting(false);
      alert('Failed to save baby record: ' + err.message);
      return;
    }
    // Always save locally
   // Always save locally
    setBabySubmitting(false);
    await refetchBabies();

    resetForm();
  };

  const resetForm = () => {
    setForm({ babyName: '', motherName: '', fatherName: '', sex: 'Male', birthWeight: '', dateOfBirth: new Date().toISOString().split('T')[0], timeOfBirth: '', amPm: 'AM', modeOfDelivery: 'NVD', cellNo: '', nicuStay: '0', address: '', patientId: '', nurseryStatus: 'observation', nurseryAdmitTime: '', apgarScore: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (record) => { setForm(record); setEditingId(record.id); setShowForm(true); };

const handlePatientSelect = (patientId) => {
  const allPts = dropdownPatients.length > 0 ? dropdownPatients : patients;
  const patient = allPts.find(p => String(p.id) === String(patientId));
    if (patient) {
      setForm({ ...form, patientId, motherName: patient.name, fatherName: patient.husbandName || '', cellNo: patient.phone || '', address: patient.address || '' });
    }
  };

 const [medicalSubmitting, setMedicalSubmitting] = useState(false);

  const handleAddMedicalRecord = async (e) => {
    e.preventDefault();
    setMedicalSubmitting(true);
    const API_TOKEN = 'A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2';
    const BASE_URL = process.env.NODE_ENV === 'production' ? 'https://api.ruqqiasultanaclinic.com' : '';

    // Resolve patientId: API babies use motherPatientId, local use patientId
    const resolvedPatientId = selectedBaby._fromApi
      ? (Number(selectedBaby.motherPatientId) || 0)
      : (Number(selectedBaby.patientId) || 0);

    const payload = {
      id: 0,
      patientId: resolvedPatientId,
      recordType: medicalForm.type || 'observation',
      description: medicalForm.description || '',
      notes: medicalForm.notes || '',
    };

    try {
      const res = await fetch(`${BASE_URL}/api/Patient/InsertBabyMedicalRecord`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': '*/*',
          'X-API-TOKEN': API_TOKEN,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      alert('Medical record saved successfully!');
      setMedicalForm({ babyId: '', type: 'observation', description: '', medication: '', dosage: '', notes: '' });
      setShowMedicalForm(false);
      setSelectedBaby(null);
    } catch (err) {
      alert('Failed to save medical record: ' + err.message);
    } finally {
      setMedicalSubmitting(false);
    }
  };

  const handleAddProformaRecord = (e) => {
    e.preventDefault();
    addBabyMedicalRecord({ 
      ...proformaForm, 
      type: 'proforma',
      date: new Date().toISOString().split('T')[0], 
      time: new Date().toLocaleTimeString() 
    });
    // Reset proforma form
    setProformaForm({
      babyId: '',
      spo2Admission: '', heartRateAdmission: '', respiratoryRateAdmission: '', presentingComplaints: '',
      generalCondition: '', pretermRisk: '', cranium: '', gruntingAssessment: '', newbornAssessment: '',
      plasma: '', micro: '', sucking: '', neonatalReflexes: '', structuralBaby: '',
      spo2Discharge: '', heartRateDischarge: '',
      treatments: []
    });
    setShowProformaForm(false);
    alert('Proforma record saved successfully!');
  };

  const addProformaTreatment = () => {
    setProformaForm({
      ...proformaForm,
      treatments: [...proformaForm.treatments, { name: '', date: '', time: '', dose: '' }]
    });
  };

  const removeProformaTreatment = (index) => {
    setProformaForm({
      ...proformaForm,
      treatments: proformaForm.treatments.filter((_, i) => i !== index)
    });
  };

  const updateProformaTreatment = (index, field, value) => {
    const newTreatments = [...proformaForm.treatments];
    newTreatments[index][field] = value;
    setProformaForm({ ...proformaForm, treatments: newTreatments });
  };

  const getBabyMedicalRecords = (babyId) => babyMedicalRecords?.filter(r => r.babyId === babyId) || [];
  const handleViewNursery = async (baby) => {
    const API_TOKEN = 'A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2';
    const BASE_URL = process.env.NODE_ENV === 'production' ? 'https://api.ruqqiasultanaclinic.com' : '';

    const resolvedPatientId = baby._fromApi
      ? (Number(baby.motherPatientId) || 0)
      : (Number(baby.patientId) || 0);

    setViewNurseryModal({ baby, records: [], nurseryExams: [] });
    setViewNurseryLoading(true);
    setViewNurseryError('');

    try {
      // Fetch both APIs in parallel
      const [medRes, examRes] = await Promise.all([
        fetch(`${BASE_URL}/api/Patient/GetBabyMedicalRecordByPatientId/${resolvedPatientId}`,
          { headers: { accept: '*/*', 'X-API-TOKEN': API_TOKEN } }),
        fetch(`${BASE_URL}/api/Patient/get-by-patient/${resolvedPatientId}`,
          { headers: { accept: '*/*', 'X-API-TOKEN': API_TOKEN } }),
      ]);

      const medData  = medRes.ok  ? await medRes.json()  : [];
      const examData = examRes.ok ? await examRes.json() : [];

      setViewNurseryModal({
        baby,
        records:      Array.isArray(medData)  ? medData  : [],
        nurseryExams: Array.isArray(examData) ? examData : [],
      });
    } catch (err) {
      setViewNurseryError(err.message);
    } finally {
      setViewNurseryLoading(false);
    }
  };

  const printNurseryRecord = (modalData) => {
    const { baby, records, nurseryExams } = modalData;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Baby Record - ${baby.babyName || 'Baby of ' + baby.motherName}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; color: #333; }
          .header { text-align: center; border-bottom: 3px solid #1e40af; padding-bottom: 15px; margin-bottom: 20px; }
          .urdu { font-size: 20px; color: #1e40af; font-weight: bold; direction: rtl; }
          .clinic-name { font-size: 20px; font-weight: bold; color: #1e40af; margin: 4px 0; }
          .subtitle { font-size: 12px; color: #555; margin: 2px 0; }
          .baby-info { background: #f0f4ff; border: 1px solid #c7d7f7; border-radius: 6px; padding: 12px; margin-bottom: 16px; }
          .baby-info h3 { color: #1e40af; font-size: 14px; margin-bottom: 8px; border-bottom: 1px solid #c7d7f7; padding-bottom: 4px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; }
          .info-grid p { font-size: 12px; }
          .section { margin-bottom: 16px; border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden; }
          .section-header { padding: 8px 12px; font-weight: bold; font-size: 13px; }
          .section-body { padding: 10px 12px; }
          .blue-header { background: #dbeafe; color: #1e40af; }
          .pink-header { background: #fce7f3; color: #9d174d; }
          .green-header { background: #d1fae5; color: #065f46; }
          .purple-header { background: #ede9fe; color: #5b21b6; }
          .data-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
          .data-item span { color: #888; font-size: 10px; display: block; }
          .data-item strong { font-size: 12px; }
          .med-record { border: 1px solid #e5e7eb; border-radius: 4px; padding: 8px; margin-bottom: 6px; }
          .badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: bold; background: #dbeafe; color: #1e40af; margin-bottom: 4px; }
          .signatures { display: flex; justify-content: space-between; margin-top: 40px; }
          .sig-box { text-align: center; }
          .sig-line { border-top: 1px solid #000; width: 120px; margin: 0 auto 4px; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="urdu">${DOCTOR_INFO.nameUrdu}</div>
          <div class="clinic-name">${DOCTOR_INFO.name}</div>
          <div class="subtitle">${DOCTOR_INFO.title}</div>
          <div class="subtitle">${DOCTOR_INFO.degrees.join(', ')}</div>
          <div class="subtitle">PMDC: ${DOCTOR_INFO.pmdc} | ${DOCTOR_INFO.college}</div>
          <div class="subtitle">${DOCTOR_INFO.address}</div>
          <div class="subtitle">Phone: ${DOCTOR_INFO.phone} | OPD: ${DOCTOR_INFO.opdTiming}</div>
        </div>

        <div class="baby-info">
          <h3>👶 Baby Information</h3>
          <div class="info-grid">
            <p><strong>Name:</strong> ${baby.babyName || 'Baby of ' + baby.motherName}</p>
            <p><strong>Certificate No:</strong> ${baby.certificateNo || '—'}</p>
            <p><strong>Mother:</strong> ${baby.motherName}</p>
            <p><strong>Father:</strong> ${baby.fatherName || '—'}</p>
            <p><strong>DOB:</strong> ${baby.dateOfBirth} at ${baby.timeOfBirth} ${baby.amPm}</p>
            <p><strong>Sex:</strong> ${baby.sex}</p>
            <p><strong>Weight:</strong> ${baby.birthWeight} kg</p>
            <p><strong>APGAR:</strong> ${baby.apgarScore || '—'}</p>
            <p><strong>Delivery:</strong> ${baby.modeOfDelivery}</p>
            <p><strong>Printed:</strong> ${new Date().toLocaleString()}</p>
          </div>
        </div>

        ${records && records.length > 0 ? `
        <div class="section">
          <div class="section-header purple-header">💊 Baby Medical Records (${records.length})</div>
          <div class="section-body">
            ${records.map(rec => `
              <div class="med-record">
                <span class="badge">${rec.recordType || 'observation'}</span>
                <span style="float:right; color:#888; font-size:10px;">Record #${rec.id}</span>
                ${rec.description ? `<p><strong>Description:</strong> ${rec.description}</p>` : ''}
                ${rec.notes ? `<p><strong>Notes:</strong> ${rec.notes}</p>` : ''}
              </div>
            `).join('')}
          </div>
        </div>
        ` : ''}

        ${nurseryExams && nurseryExams.length > 0 ? nurseryExams.map(rec => `
        <div class="section">
          <div class="section-header blue-header">🏥 Nursery Examination — Record #${rec.id} (${rec.createdDate ? new Date(rec.createdDate).toLocaleDateString() : '—'})</div>
          <div class="section-body">
            <div class="section" style="margin-bottom:10px;">
              <div class="section-header blue-header" style="font-size:11px;">Baby Examined</div>
              <div class="section-body">
                <div class="data-grid">
                  ${[['Mode of Delivery', rec.modeOfDelivery], ['Gender', rec.gender], ['APGAR Score', rec.apgarScore], ['Cry', rec.immediateCryDelayedCry], ['Weight (kg)', rec.weightKg], ['Color', rec.color], ['A/F', rec.af], ['Anal Opening', rec.analOpening], ['Back & Spine', rec.backSpine], ['Genitalia', rec.genitalia], ['Eyes/Nose/Ear', rec.eyesNoseEar], ['Cord', rec.cord], ['CNS', rec.cns], ['CVS', rec.cvs], ['Oral Cavity', rec.oralCavity], ['Limbs', rec.upperLowerLimbs]].map(([label, val]) => `
                    <div class="data-item"><span>${label}</span><strong>${val && val !== 'string' && val !== 0 ? val : '—'}</strong></div>
                  `).join('')}
                </div>
              </div>
            </div>
            <div class="section" style="margin-bottom:10px;">
              <div class="section-header pink-header" style="font-size:11px;">Risk Factors</div>
              <div class="section-body">
                <div class="data-grid">
                  ${[['Gestation', rec.periodOfGestation], ['Leaking Hx', rec.historyOfLeaking], ['Tetanus', rec.tetanusVaccination], ['Diabetes Hx', rec.historyOfDiabetes], ['Hypertension Hx', rec.historyOfHypertension], ['Twins Hx', rec.historyOfTwins], ['IUGR Hx', rec.historyOfIUGR], ['Preterm Hx', rec.historyOfPreterm], ['IUD Hx', rec.historyOfIUD], ['Anomalous Baby', rec.previousAnomalousBaby], ['Mother Blood Group', rec.motherBloodGroup], ['HbsAg', rec.hBsAg], ['Anti HCV', rec.antiHCV]].map(([label, val]) => `
                    <div class="data-item"><span>${label}</span><strong>${val && val !== 'string' ? val : '—'}</strong></div>
                  `).join('')}
                </div>
              </div>
            </div>
            ${(rec.advice || rec.injections || rec.others || rec.caseOfReferral) ? `
            <div class="section" style="margin-bottom:0;">
              <div class="section-header green-header" style="font-size:11px;">Advice & Treatment</div>
              <div class="section-body">
                ${rec.advice ? `<p><strong>Advice:</strong> ${rec.advice}</p>` : ''}
                ${rec.injections && rec.injections !== 'string' ? `<p><strong>Injections:</strong> ${rec.injections}</p>` : ''}
                ${rec.others && rec.others !== 'string' ? `<p><strong>Others:</strong> ${rec.others}</p>` : ''}
                ${rec.caseOfReferral && rec.caseOfReferral !== 'string' ? `<p><strong>Referral:</strong> ${rec.caseOfReferral}</p>` : ''}
              </div>
            </div>
            ` : ''}
          </div>
        </div>
        `).join('') : ''}

        <div class="signatures">
          <div class="sig-box"><div class="sig-line"></div><div>Nurse</div></div>
          <div class="sig-box"><div class="sig-line"></div><div>Doctor / ${DOCTOR_INFO.name}</div></div>
        </div>

        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };
  const handleShiftToMother = async (baby) => {
    if (!window.confirm(`Shift ${baby.babyName || 'baby'} to mother?\n\nBaby will be marked as discharged with mother.`)) return;

    // Extract numeric ID from API babies (id format is "api-4973-...")
    if (baby._fromApi) {
const numericId = baby._apiId || Number(String(baby.id).split('-')[1]);
console.log('Baby ID debug:', baby.id, '→ numeric:', numericId);
      const API_TOKEN = 'A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2';
      const BASE_URL = process.env.NODE_ENV === 'production' ? 'https://api.ruqqiasultanaclinic.com' : '';
      try {
        const res = await fetch(`${BASE_URL}/api/Patient/babyrecordupdate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'accept': '*/*', 'X-API-TOKEN': API_TOKEN },
          body: JSON.stringify({ id: numericId, babyStatus: 'with mother' }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        console.log('Baby status updated to with mother');
      } catch (err) {
        alert('Failed to update baby status: ' + err.message);
        return;
      }
      await refetchBabies();
    } else {
      updateBirthRecord(baby.id, {
        daycareStatus: 'with-mother',
        nurseryStatus: 'discharged',
        nurseryDischargeTime: new Date().toLocaleTimeString()
      });
    }

    alert('Baby shifted to mother successfully!');
  };

  const handleInitiateNurseryShift = (baby) => {
    setShiftingBaby(baby);
    // Pre-fill nursery form with existing baby data
    setNurseryForm({
      ...nurseryForm,
      modeOfDelivery: baby.modeOfDelivery || '',
      gender: baby.sex || '',
      apgarScore: baby.apgarScore || '',
      weight: baby.birthWeight || '',
      motherBloodGroup: '',
      periodOfGestation: ''
    });
    setShowNurseryForm(true);
  };
  const [babySubmitting, setBabySubmitting] = useState(false);

  const [nurserySubmitting, setNurserySubmitting] = useState(false);

  const handleNurseryFormSubmit = async (e) => {
    e.preventDefault();
    setNurserySubmitting(true);

    // Resolve patientId: from API baby use motherPatientId, from local use patientId
   const resolvedPatientId = shiftingBaby._fromApi
      ? (Number(shiftingBaby.motherPatientId) || Number(String(shiftingBaby.id).split('-')[1]) || 0)
      : (Number(shiftingBaby.patientId) || 0);

    const API_TOKEN = 'A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2';
    const BASE_URL = process.env.NODE_ENV === 'production' ? 'https://api.ruqqiasultanaclinic.com' : '';

    const payload = {
      model: '',
      id: 0,
      patientId: resolvedPatientId,
      mrNo: shiftingBaby.certificateNo || '',
      modeOfDelivery: nurseryForm.modeOfDelivery || '',
      gender: nurseryForm.gender || '',
 apgarScore: String(nurseryForm.apgarScore || ''),      immediateCryDelayedCry: nurseryForm.immediateCry || '',
      weightKg: Number(nurseryForm.weight) || 0,
      color: nurseryForm.color || '',
      af: nurseryForm.af || '',
      analOpening: nurseryForm.analOpening || '',
      backSpine: nurseryForm.backSpine || '',
      genitalia: nurseryForm.genitelia || '',
      eyesNoseEar: nurseryForm.eyesNoseEar || '',
      cord: nurseryForm.cord || '',
      cns: nurseryForm.cns || '',
      cvs: nurseryForm.cvs || '',
      oralCavity: nurseryForm.oralCavity || '',
      upperLowerLimbs: nurseryForm.upperLowerLimbs || '',
      periodOfGestation: nurseryForm.periodOfGestation || '',
      historyOfLeaking: nurseryForm.historyOfLeaking || '',
      tetanusVaccination: nurseryForm.tetanusVaccination || '',
      historyOfDiabetes: nurseryForm.historyOfDiabetes || '',
      historyOfHypertension: nurseryForm.historyOfHypertension || '',
      historyOfTwins: nurseryForm.historyOfTwins || '',
      historyOfIUGR: nurseryForm.historyOfIUGR || '',
      historyOfPreterm: nurseryForm.historyOfPreterm || '',
      historyOfIUD: nurseryForm.historyOfIUD || '',
      previousAnomalousBaby: nurseryForm.previousAnomalousBaby || '',
      motherBloodGroup: nurseryForm.motherBloodGroup || '',
      hBsAg: nurseryForm.hbsAg || '',
      antiHCV: nurseryForm.antiHCV || '',
      advice: nurseryForm.advice || '',
      injections: nurseryForm.injections || '',
      others: nurseryForm.others || '',
      caseOfReferral: nurseryForm.causeOfReferral || '',
      createdDate: new Date().toISOString(),
      createdBy: 'Doctor',
      modifiedDate: new Date().toISOString(),
      modifiedBy: 'Doctor',
    };

    try {
      const res = await fetch(`${BASE_URL}/api/Patient/createbabyexamination`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': '*/*',
          'X-API-TOKEN': API_TOKEN,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      console.log('Baby examination saved to API:', result);
    } catch (err) {
      console.warn('Nursery API save failed:', err.message);
    }

  
    setNurserySubmitting(false);
    alert('Baby admitted to nursery successfully!');
    setShowNurseryForm(false);
    setShiftingBaby(null);
    setNurseryForm({
      modeOfDelivery: '', gender: '', apgarScore: '', immediateCry: '', weight: '', color: '', af: '',
      analOpening: '', backSpine: '', genitelia: '', eyesNoseEar: '', cord: '', cns: '', cvs: '',
      oralCavity: '', upperLowerLimbs: '', periodOfGestation: '', historyOfLeaking: '',
      tetanusVaccination: '', historyOfDiabetes: '', historyOfHypertension: '', historyOfTwins: '',
      historyOfIUGR: '', historyOfPreterm: '', historyOfIUD: '', previousAnomalousBaby: '',
      motherBloodGroup: '', hbsAg: '', antiHCV: '', advice: '', injections: 'Vitamin K 10units I/M Stat',
      others: '', causeOfReferral: '', doctorName: DOCTOR_INFO.name
    });
  };
  const handleNurseryStatusChange = (babyId, status) => {
    const baby = birthRecords.find(b => b.id === babyId);
    if (baby) {
      updateBirthRecord(babyId, { 
        ...baby, 
        nurseryStatus: status, 
        daycareStatus: status === 'discharged' ? 'with-mother' : baby.daycareStatus,
        nurseryDischargeTime: status === 'discharged' ? new Date().toLocaleTimeString() : baby.nurseryDischargeTime 
      });
    }
  };

  const getDaycareTimeRemaining = (baby) => {
    if (!baby.daycareAdmissionTime || baby.daycareStatus !== 'in-daycare') return null;
    const admissionTime = new Date(baby.daycareAdmissionTime);
    const now = new Date();
    const diffMs = now - admissionTime;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const remainingHours = Math.max(0, 3 - diffHours);
    const remainingMinutes = Math.max(0, 60 - diffMinutes);
    
    const totalRemainingMs = Math.max(0, 3 * 60 * 60 * 1000 - diffMs);
    if (totalRemainingMs === 0) return 'Time to shift!';
    const remMin = Math.floor(totalRemainingMs / 60000);
    const remSec = Math.floor((totalRemainingMs % 60000) / 1000);
    return `${remMin}m ${remSec}s remaining`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">👶 Baby Records & Nursery</h2>
        <div className="flex items-center gap-2">
          {babiesLoading && <span className="text-xs text-blue-500 animate-pulse">🔄 Loading...</span>}
          {!babiesLoading && apiBabies.length > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-green-600">✅ Live ({apiBabies.length} from API)</span>
              <button onClick={refetchBabies} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">🔄</button>
            </div>
          )}
          <button onClick={() => setShowForm(true)} className="btn-primary text-sm">+ Add Baby Record</button>
        </div>
      </div>

     

      {/* Baby Birth Form */}
      {showForm && (
        <div className="card p-6">
          <h3 className="font-bold text-gray-900 mb-4">{editingId ? 'Edit' : 'Add'} Baby Record</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
           <div>
  <label className="block text-sm font-medium mb-1">Select Mother (Patient)</label>
  <PatientSearchDropdown
    patients={dropdownPatients.length > 0 ? dropdownPatients : admittedPatients}
    value={form.patientId}
    onChange={(id) => handlePatientSelect(id)}
  />
</div>
            <div className="grid md:grid-cols-3 gap-4">
              <div><label className="block text-sm font-medium mb-1">Baby Name</label><input type="text" value={form.babyName} onChange={(e) => setForm({...form, babyName: e.target.value})} className="input-field" placeholder="Baby of [Mother]" /></div>
              <div>
                <label className="block text-sm font-medium mb-1">Gender *</label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2"><input type="radio" name="sex" value="Male" checked={form.sex === 'Male'} onChange={(e) => setForm({...form, sex: e.target.value})} /> Male</label>
                  <label className="flex items-center gap-2"><input type="radio" name="sex" value="Female" checked={form.sex === 'Female'} onChange={(e) => setForm({...form, sex: e.target.value})} /> Female</label>
                </div>
              </div>
              <div><label className="block text-sm font-medium mb-1">APGAR Score</label><input type="text" value={form.apgarScore} onChange={(e) => setForm({...form, apgarScore: e.target.value})} className="input-field" placeholder="e.g., 9/10" /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Mother's Name *</label><input type="text" value={form.motherName} onChange={(e) => setForm({...form, motherName: e.target.value})} className="input-field" required /></div>
              <div><label className="block text-sm font-medium mb-1">Father's Name *</label><input type="text" value={form.fatherName} onChange={(e) => setForm({...form, fatherName: e.target.value})} className="input-field" required /></div>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              <div><label className="block text-sm font-medium mb-1">Date of Birth *</label><input type="date" value={form.dateOfBirth} onChange={(e) => setForm({...form, dateOfBirth: e.target.value})} className="input-field" required /></div>
              <div><label className="block text-sm font-medium mb-1">Time *</label><input type="time" value={form.timeOfBirth} onChange={(e) => setForm({...form, timeOfBirth: e.target.value})} className="input-field" required /></div>
              <div><label className="block text-sm font-medium mb-1">AM/PM</label><select value={form.amPm} onChange={(e) => setForm({...form, amPm: e.target.value})} className="input-field"><option>AM</option><option>PM</option></select></div>
              <div><label className="block text-sm font-medium mb-1">Weight (kg) *</label><input type="text" value={form.birthWeight} onChange={(e) => setForm({...form, birthWeight: e.target.value})} className="input-field" required /></div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div><label className="block text-sm font-medium mb-1">Mode of Delivery</label><select value={form.modeOfDelivery} onChange={(e) => setForm({...form, modeOfDelivery: e.target.value})} className="input-field"><option value="NVD">NVD</option><option value="LSCS">LSCS (C-Section)</option><option value="Assisted">Assisted</option></select></div>
              <div><label className="block text-sm font-medium mb-1">Cell No</label><input type="tel" value={form.cellNo} onChange={(e) => setForm({...form, cellNo: e.target.value})} className="input-field" /></div>
            </div>
            <div><label className="block text-sm font-medium mb-1">Address</label><input type="text" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="input-field" /></div>
            <div className="flex gap-2">
<button type="submit" disabled={babySubmitting} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60">
  {babySubmitting ? (
    <><span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span> Saving...</>
  ) : (editingId ? 'Update' : 'Save') + ' Record'}
</button>              <button type="button" onClick={resetForm} className="btn-secondary text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Nursery Admission Form Modal */}
      {showNurseryForm && shiftingBaby && (
        <div className="modal-overlay" onClick={() => setShowNurseryForm(false)}>
          <div className="modal-content p-6 max-w-6xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-bold">NICU/Nursery Admission Form</h3>
                <p className="text-sm text-gray-600">Baby: {shiftingBaby.babyName || `Baby of ${shiftingBaby.motherName}`}</p>
              </div>
              <button onClick={() => setShowNurseryForm(false)} className="text-gray-500 text-2xl">×</button>
            </div>

            <form onSubmit={handleNurseryFormSubmit} className="space-y-6">
              {/* Baby Examined Section */}
              <div className="card p-4 bg-blue-50">
                <h4 className="font-bold text-blue-900 mb-3">BABY EXAMINED</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Mode of Delivery *</label><input type="text" value={nurseryForm.modeOfDelivery} onChange={(e) => setNurseryForm({...nurseryForm, modeOfDelivery: e.target.value})} className="input-field" required /></div>
                  <div><label className="block text-sm font-medium mb-1">Gender *</label><input type="text" value={nurseryForm.gender} onChange={(e) => setNurseryForm({...nurseryForm, gender: e.target.value})} className="input-field" required /></div>
                  <div><label className="block text-sm font-medium mb-1">Apgar Score *</label><input type="text" value={nurseryForm.apgarScore} onChange={(e) => setNurseryForm({...nurseryForm, apgarScore: e.target.value})} className="input-field" required /></div>
                  <div><label className="block text-sm font-medium mb-1">Immediate Cry / Delayed Cry</label><input type="text" value={nurseryForm.immediateCry} onChange={(e) => setNurseryForm({...nurseryForm, immediateCry: e.target.value})} className="input-field" placeholder="Immediate/Delayed" /></div>
                  <div><label className="block text-sm font-medium mb-1">Weight (kg) *</label><input type="text" value={nurseryForm.weight} onChange={(e) => setNurseryForm({...nurseryForm, weight: e.target.value})} className="input-field" required /></div>
                  <div><label className="block text-sm font-medium mb-1">Color</label><input type="text" value={nurseryForm.color} onChange={(e) => setNurseryForm({...nurseryForm, color: e.target.value})} className="input-field" placeholder="e.g., Pink" /></div>
                  <div><label className="block text-sm font-medium mb-1">A/F</label><input type="text" value={nurseryForm.af} onChange={(e) => setNurseryForm({...nurseryForm, af: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Anal Opening</label><input type="text" value={nurseryForm.analOpening} onChange={(e) => setNurseryForm({...nurseryForm, analOpening: e.target.value})} className="input-field" placeholder="Normal/Abnormal" /></div>
                  <div><label className="block text-sm font-medium mb-1">Back & Spine</label><input type="text" value={nurseryForm.backSpine} onChange={(e) => setNurseryForm({...nurseryForm, backSpine: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Genitelia</label><input type="text" value={nurseryForm.genitelia} onChange={(e) => setNurseryForm({...nurseryForm, genitelia: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Eyes, Nose & Ear</label><input type="text" value={nurseryForm.eyesNoseEar} onChange={(e) => setNurseryForm({...nurseryForm, eyesNoseEar: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Cord</label><input type="text" value={nurseryForm.cord} onChange={(e) => setNurseryForm({...nurseryForm, cord: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">CNS</label><input type="text" value={nurseryForm.cns} onChange={(e) => setNurseryForm({...nurseryForm, cns: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">CVS</label><input type="text" value={nurseryForm.cvs} onChange={(e) => setNurseryForm({...nurseryForm, cvs: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Oral Cavity</label><input type="text" value={nurseryForm.oralCavity} onChange={(e) => setNurseryForm({...nurseryForm, oralCavity: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Upper and Lower Limbs</label><input type="text" value={nurseryForm.upperLowerLimbs} onChange={(e) => setNurseryForm({...nurseryForm, upperLowerLimbs: e.target.value})} className="input-field" /></div>
                </div>
              </div>

              {/* Risk Factors Section */}
              <div className="card p-4 bg-pink-50">
                <h4 className="font-bold text-pink-900 mb-3">RISK FACTORS</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Period of Gestation</label><input type="text" value={nurseryForm.periodOfGestation} onChange={(e) => setNurseryForm({...nurseryForm, periodOfGestation: e.target.value})} className="input-field" placeholder="e.g., 40 weeks" /></div>
                  <div><label className="block text-sm font-medium mb-1">History of Leaking</label><input type="text" value={nurseryForm.historyOfLeaking} onChange={(e) => setNurseryForm({...nurseryForm, historyOfLeaking: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Tetanus Vaccination</label><input type="text" value={nurseryForm.tetanusVaccination} onChange={(e) => setNurseryForm({...nurseryForm, tetanusVaccination: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">History of Diabetes</label><input type="text" value={nurseryForm.historyOfDiabetes} onChange={(e) => setNurseryForm({...nurseryForm, historyOfDiabetes: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">History of Hypertension</label><input type="text" value={nurseryForm.historyOfHypertension} onChange={(e) => setNurseryForm({...nurseryForm, historyOfHypertension: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">History of Twins</label><input type="text" value={nurseryForm.historyOfTwins} onChange={(e) => setNurseryForm({...nurseryForm, historyOfTwins: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">History of IUGR</label><input type="text" value={nurseryForm.historyOfIUGR} onChange={(e) => setNurseryForm({...nurseryForm, historyOfIUGR: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">History of Preterm</label><input type="text" value={nurseryForm.historyOfPreterm} onChange={(e) => setNurseryForm({...nurseryForm, historyOfPreterm: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">History of IUD</label><input type="text" value={nurseryForm.historyOfIUD} onChange={(e) => setNurseryForm({...nurseryForm, historyOfIUD: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Previous Anomalous Baby</label><input type="text" value={nurseryForm.previousAnomalousBaby} onChange={(e) => setNurseryForm({...nurseryForm, previousAnomalousBaby: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Mother Blood Group</label><input type="text" value={nurseryForm.motherBloodGroup} onChange={(e) => setNurseryForm({...nurseryForm, motherBloodGroup: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">HbsAg</label><input type="text" value={nurseryForm.hbsAg} onChange={(e) => setNurseryForm({...nurseryForm, hbsAg: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Anti HCV</label><input type="text" value={nurseryForm.antiHCV} onChange={(e) => setNurseryForm({...nurseryForm, antiHCV: e.target.value})} className="input-field" /></div>
                </div>
              </div>

              {/* Advice Section */}
              <div className="card p-4 bg-green-50">
                <h4 className="font-bold text-green-900 mb-3">ADVICE & TREATMENT</h4>
                <div className="space-y-4">
                  <div><label className="block text-sm font-medium mb-1">Advice</label><textarea value={nurseryForm.advice} onChange={(e) => setNurseryForm({...nurseryForm, advice: e.target.value})} className="input-field" rows="2" placeholder="General advice for baby care"></textarea></div>
                  <div><label className="block text-sm font-medium mb-1">Injections</label><input type="text" value={nurseryForm.injections} onChange={(e) => setNurseryForm({...nurseryForm, injections: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Others</label><textarea value={nurseryForm.others} onChange={(e) => setNurseryForm({...nurseryForm, others: e.target.value})} className="input-field" rows="2" placeholder="Other treatments or notes"></textarea></div>
                  <div><label className="block text-sm font-medium mb-1">Cause of Referral</label><input type="text" value={nurseryForm.causeOfReferral} onChange={(e) => setNurseryForm({...nurseryForm, causeOfReferral: e.target.value})} className="input-field" placeholder="If referred to specialist" /></div>
                </div>
              </div>

              <div className="flex gap-2">
                <button type="submit" disabled={nurserySubmitting} className="btn-primary flex items-center gap-2 disabled:opacity-60">
                  {nurserySubmitting ? (
                    <><span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span> Saving...</>
                  ) : 'Admit to Nursery'}
                </button>
                <button type="button" disabled={nurserySubmitting} onClick={() => setShowNurseryForm(false)} className="btn-secondary disabled:opacity-60">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Baby Medical Record Form */}
      {showMedicalForm && selectedBaby && (
        <div className="card p-6 border-2 border-pink-300">
          <h3 className="font-bold text-gray-900 mb-4">Add Medical Record for {selectedBaby.babyName || `Baby of ${selectedBaby.motherName}`}</h3>
          <form onSubmit={handleAddMedicalRecord} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Record Type *</label>
                <select value={medicalForm.type} onChange={(e) => setMedicalForm({...medicalForm, type: e.target.value})} className="input-field" required>
                  <option value="observation">Observation Note</option>
                  <option value="medication">Medication Given</option>
                  <option value="treatment">Treatment/Procedure</option>
                  <option value="feeding">Feeding Record</option>
                  <option value="vitals">Vitals Check</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium mb-1">Description *</label><input type="text" value={medicalForm.description} onChange={(e) => setMedicalForm({...medicalForm, description: e.target.value})} className="input-field" required /></div>
            </div>
            {medicalForm.type === 'medication' && (
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Medication Name</label><input type="text" value={medicalForm.medication} onChange={(e) => setMedicalForm({...medicalForm, medication: e.target.value})} className="input-field" /></div>
                <div><label className="block text-sm font-medium mb-1">Dosage</label><input type="text" value={medicalForm.dosage} onChange={(e) => setMedicalForm({...medicalForm, dosage: e.target.value})} className="input-field" /></div>
              </div>
            )}
            <div><label className="block text-sm font-medium mb-1">Notes</label><textarea value={medicalForm.notes} onChange={(e) => setMedicalForm({...medicalForm, notes: e.target.value})} className="input-field" rows="2" /></div>
           <div className="flex gap-2">
              <button type="submit" disabled={medicalSubmitting} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60">
                {medicalSubmitting ? (
                  <><span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span> Saving...</>
                ) : 'Save Record'}
              </button>
              <button type="button" onClick={() => { setShowMedicalForm(false); setSelectedBaby(null); }} className="btn-secondary text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Baby Proforma Form - Vitals & Treatment Tracking */}
      {showProformaForm && selectedBaby && (
        <div className="modal-overlay" onClick={() => setShowProformaForm(false)}>
          <div className="modal-content p-6 max-w-6xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-bold">Baby Proforma - Vitals & Treatment Tracking</h3>
                <p className="text-sm text-gray-600">Baby: {selectedBaby.babyName || `Baby of ${selectedBaby.motherName}`}</p>
              </div>
              <button onClick={() => setShowProformaForm(false)} className="text-gray-500 text-2xl">×</button>
            </div>

            <form onSubmit={handleAddProformaRecord} className="space-y-6">
              {/* Vitals at Admission */}
              <div className="card p-4 bg-blue-50">
                <h4 className="font-bold text-blue-900 mb-3">Vitals at Admission</h4>
                <div className="grid md:grid-cols-4 gap-4">
                  <div><label className="block text-sm font-medium mb-1">SpO2</label><input type="text" value={proformaForm.spo2Admission} onChange={(e) => setProformaForm({...proformaForm, spo2Admission: e.target.value})} className="input-field" placeholder="e.g., 98%" /></div>
                  <div><label className="block text-sm font-medium mb-1">Heart Rate (HR)</label><input type="text" value={proformaForm.heartRateAdmission} onChange={(e) => setProformaForm({...proformaForm, heartRateAdmission: e.target.value})} className="input-field" placeholder="e.g., 140 bpm" /></div>
                  <div><label className="block text-sm font-medium mb-1">Respiratory Rate (RR)</label><input type="text" value={proformaForm.respiratoryRateAdmission} onChange={(e) => setProformaForm({...proformaForm, respiratoryRateAdmission: e.target.value})} className="input-field" placeholder="e.g., 45/min" /></div>
                  <div><label className="block text-sm font-medium mb-1">Presenting Complaints (P/C)</label><input type="text" value={proformaForm.presentingComplaints} onChange={(e) => setProformaForm({...proformaForm, presentingComplaints: e.target.value})} className="input-field" /></div>
                </div>
              </div>

              {/* General Physical Examination */}
              <div className="card p-4 bg-green-50">
                <h4 className="font-bold text-green-900 mb-3">General Physical Examination</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div><label className="block text-sm font-medium mb-1">General Condition</label><input type="text" value={proformaForm.generalCondition} onChange={(e) => setProformaForm({...proformaForm, generalCondition: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Preterm / Risk</label><input type="text" value={proformaForm.pretermRisk} onChange={(e) => setProformaForm({...proformaForm, pretermRisk: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Cranium</label><input type="text" value={proformaForm.cranium} onChange={(e) => setProformaForm({...proformaForm, cranium: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Grunting Assessment</label><input type="text" value={proformaForm.gruntingAssessment} onChange={(e) => setProformaForm({...proformaForm, gruntingAssessment: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Newborn Assessment</label><input type="text" value={proformaForm.newbornAssessment} onChange={(e) => setProformaForm({...proformaForm, newbornAssessment: e.target.value})} className="input-field" /></div>
                </div>
              </div>

              {/* Home Treatment */}
              <div className="card p-4 bg-purple-50">
                <h4 className="font-bold text-purple-900 mb-3">Home Treatment / Assessment</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div><label className="block text-sm font-medium mb-1">Plasma</label><input type="text" value={proformaForm.plasma} onChange={(e) => setProformaForm({...proformaForm, plasma: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Micro</label><input type="text" value={proformaForm.micro} onChange={(e) => setProformaForm({...proformaForm, micro: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Sucking</label><input type="text" value={proformaForm.sucking} onChange={(e) => setProformaForm({...proformaForm, sucking: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Neonatal Reflexes</label><input type="text" value={proformaForm.neonatalReflexes} onChange={(e) => setProformaForm({...proformaForm, neonatalReflexes: e.target.value})} className="input-field" /></div>
                  <div><label className="block text-sm font-medium mb-1">Structural Baby</label><input type="text" value={proformaForm.structuralBaby} onChange={(e) => setProformaForm({...proformaForm, structuralBaby: e.target.value})} className="input-field" /></div>
                </div>
              </div>

              {/* Vitals at Discharge */}
              <div className="card p-4 bg-pink-50">
                <h4 className="font-bold text-pink-900 mb-3">Vitals at Discharge</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">SpO2</label><input type="text" value={proformaForm.spo2Discharge} onChange={(e) => setProformaForm({...proformaForm, spo2Discharge: e.target.value})} className="input-field" placeholder="e.g., 98%" /></div>
                  <div><label className="block text-sm font-medium mb-1">Heart Rate (HR)</label><input type="text" value={proformaForm.heartRateDischarge} onChange={(e) => setProformaForm({...proformaForm, heartRateDischarge: e.target.value})} className="input-field" placeholder="e.g., 135 bpm" /></div>
                </div>
              </div>

              {/* Treatment Given */}
              <div className="card p-4 bg-yellow-50">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-bold text-yellow-900">Treatment Given</h4>
                  <button type="button" onClick={addProformaTreatment} className="btn-success text-xs">+ Add Treatment</button>
                </div>
                <div className="space-y-3">
                  {proformaForm.treatments.map((treatment, index) => (
                    <div key={index} className="grid md:grid-cols-5 gap-2 items-end p-3 bg-white rounded border">
                      <div><label className="block text-xs font-medium mb-1">Treatment Name *</label><input type="text" value={treatment.name} onChange={(e) => updateProformaTreatment(index, 'name', e.target.value)} className="input-field text-sm" placeholder="e.g., Inj Antibiotic 250mg IV" required /></div>
                      <div><label className="block text-xs font-medium mb-1">Date</label><input type="date" value={treatment.date} onChange={(e) => updateProformaTreatment(index, 'date', e.target.value)} className="input-field text-sm" /></div>
                      <div><label className="block text-xs font-medium mb-1">Time</label><input type="time" value={treatment.time} onChange={(e) => updateProformaTreatment(index, 'time', e.target.value)} className="input-field text-sm" /></div>
                      <div><label className="block text-xs font-medium mb-1">Dose/Details</label><input type="text" value={treatment.dose} onChange={(e) => updateProformaTreatment(index, 'dose', e.target.value)} className="input-field text-sm" placeholder="Optional details" /></div>
                      <div><button type="button" onClick={() => removeProformaTreatment(index)} className="btn-danger text-xs w-full">Remove</button></div>
                    </div>
                  ))}
                  {proformaForm.treatments.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">No treatments added yet. Click "+ Add Treatment" to add.</p>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <button type="submit" className="btn-primary">Save Proforma Record</button>
                <button type="button" onClick={() => setShowProformaForm(false)} className="btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Baby Cards */}
     {/* Baby Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {babiesLoading ? (
          <div className="col-span-2 flex flex-col items-center justify-center py-12 gap-3">
            <span className="animate-spin inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></span>
            <p className="text-gray-500 text-sm">Loading baby records from server...</p>
          </div>
        ) : allBabyRecords.length === 0 ? (
          <div className="col-span-2 text-center py-12 text-gray-500">No baby records yet</div>
        ) : allBabyRecords.map(record => {
          const medRecords = getBabyMedicalRecords(record.id);
          
          return (
           <div key={record.id} className={`card p-4 ${
  record.nurseryStatus === 'discharged' ? 'border-2 border-green-400' :
  record.daycareStatus === 'in-nursery' ? 'border-2 border-orange-400' : 
  'border-2 border-yellow-400'
}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{record.sex === 'Male' ? '👶🏻' : '👶🏻'}</span>
                  <div>
                    <p className="font-bold text-gray-900">{record.babyName || `Baby of ${record.motherName}`}</p>
                    <p className="text-xs text-blue-600">{record.certificateNo}</p>
                  </div>
                </div>
                <div className="text-right">
                <span className={`px-2 py-1 rounded text-xs font-medium block mb-1 ${
  record.nurseryStatus === 'discharged' ? 'bg-green-100 text-green-700' :
  record.daycareStatus === 'in-nursery' ? 'bg-orange-100 text-orange-700' : 
  'bg-yellow-100 text-yellow-700'
}`}>
                    {record.daycareStatus === 'in-nursery' ? '🏥 In Nursery' : 
                     record.daycareStatus === 'in-daycare' ? '👁️ Daycare' : 
                     '✅ With Mother'}
                  </span>
                  
                </div>
              </div>
              <div className="text-sm space-y-1 text-gray-600">
                <p><span className="text-gray-400">Mother:</span> {record.motherName} | <span className="text-gray-400">Father:</span> {record.fatherName}</p>
                <p><span className="text-gray-400">DOB:</span> {record.dateOfBirth} at {record.timeOfBirth} {record.amPm}</p>
                <p><span className="text-gray-400">Sex:</span> {record.sex} | <span className="text-gray-400">Weight:</span> {record.birthWeight} kg | <span className="text-gray-400">APGAR:</span> {record.apgarScore || '-'}</p>
                <p><span className="text-gray-400">Delivery:</span> {record.modeOfDelivery}</p>
                {record.nurseryAdmissionTime && <p><span className="text-gray-400">Nursery Admit:</span> {new Date(record.nurseryAdmissionTime).toLocaleString()}</p>}
              </div>

              {/* Baby Medical Records */}
              {medRecords.length > 0 && (
                <div className="mt-3 border-t pt-3">
                  <p className="text-xs font-bold text-gray-700 mb-2">📋 Medical Records ({medRecords.length})</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {medRecords.map((r, i) => (
                      <div key={i} className="text-xs bg-gray-50 p-2 rounded">
                        <span className="font-medium">{r.date} {r.time}</span> - <span className="text-blue-600">{r.type}</span>: {r.description}
                        {r.medication && <span className="text-green-600"> | {r.medication} {r.dosage}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-3 flex gap-2 flex-wrap">
                <button onClick={() => handleEdit(record)} className="text-xs px-3 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200">Edit</button>

<button onClick={() => handleViewNursery(record)} className="text-xs px-3 py-1 bg-indigo-100 text-indigo-600 rounded hover:bg-indigo-200">👁 View</button>
                {record.nurseryStatus !== 'discharged' && (
  <button onClick={() => { setSelectedBaby(record); setMedicalForm({...medicalForm, babyId: record.id}); setShowMedicalForm(true); }} className="text-xs px-3 py-1 bg-pink-100 text-pink-600 rounded hover:bg-pink-200">+ Medical Record</button>
)}
{record.nurseryStatus !== 'discharged' && (
  <button onClick={() => handleShiftToMother(record)} className="text-xs px-3 py-1 bg-green-100 text-green-600 rounded hover:bg-green-200">Shift to Mother</button>
)}
{record.nurseryStatus !== 'discharged' && (
  <button onClick={() => handleInitiateNurseryShift(record)} className="text-xs px-3 py-1 bg-orange-100 text-orange-600 rounded hover:bg-orange-200">Shift to Nursery</button>
)}
{record.daycareStatus === 'in-nursery' && (
  <button onClick={() => handleNurseryStatusChange(record.id, 'discharged')} className="text-xs px-3 py-1 bg-green-100 text-green-600 rounded hover:bg-green-200">Discharge from Nursery</button>
)}
              </div>
            </div>
          );
        })}
      </div>
{/* View Nursery Record Modal */}
      {viewNurseryModal && (
        <div className="modal-overlay" onClick={() => setViewNurseryModal(null)}>
          <div className="modal-content p-6 max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-bold">NICU/Nursery Record</h3>
                <p className="text-sm text-gray-600">
                  Baby: {viewNurseryModal.baby.babyName || `Baby of ${viewNurseryModal.baby.motherName}`}
                </p>
              </div>
              <button onClick={() => setViewNurseryModal(null)} className="text-gray-500 text-2xl">×</button>
            </div>

            {viewNurseryLoading && (
              <div className="text-center py-12">
                <span className="animate-spin inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mb-3"></span>
                <p className="text-gray-500">Loading nursery records...</p>
              </div>
            )}

            {viewNurseryError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm mb-4">
                ⚠️ {viewNurseryError}
              </div>
            )}

            {!viewNurseryLoading && !viewNurseryError &&
              viewNurseryModal.records.length === 0 &&
              (viewNurseryModal.nurseryExams || []).length === 0 && (
              <div className="text-center py-12 text-gray-500">No records found for this baby.</div>
            )}

            {/* Baby Medical Records Section */}
            {!viewNurseryLoading && viewNurseryModal.records.length > 0 && (
              <div className="mb-6">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <span className="px-2 py-1 bg-pink-100 text-pink-700 rounded text-xs">💊 Baby Medical Records</span>
                  <span className="text-xs text-gray-400">{viewNurseryModal.records.length} record(s)</span>
                </h4>
                <div className="space-y-3">
                  {viewNurseryModal.records.map((rec, idx) => (
                    <div key={rec.id || idx} className="card p-4 border border-gray-200">
                      <div className="flex justify-between items-start mb-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          {rec.recordType || 'observation'}
                        </span>
                        <span className="text-xs text-gray-400">Record #{rec.id}</span>
                      </div>
                      {rec.description && (
                        <div className="mb-2">
                          <span className="text-xs text-gray-500 font-medium">Description:</span>
                          <p className="text-sm text-gray-800 mt-0.5">{rec.description}</p>
                        </div>
                      )}
                      {rec.notes && (
                        <div>
                          <span className="text-xs text-gray-500 font-medium">Notes:</span>
                          <p className="text-sm text-gray-800 mt-0.5">{rec.notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Nursery Examination Records Section */}
            {!viewNurseryLoading && (viewNurseryModal.nurseryExams || []).length > 0 && (
              <div>
                <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">🏥 Nursery Examination Records</span>
                  <span className="text-xs text-gray-400">{viewNurseryModal.nurseryExams.length} record(s)</span>
                </h4>
                <div className="space-y-4">
                  {viewNurseryModal.nurseryExams.map((rec, idx) => (
                    <div key={rec.id || idx} className="space-y-3 border border-gray-200 rounded-lg p-4">
                      <p className="text-xs text-gray-400">Record #{rec.id} — {rec.createdDate ? new Date(rec.createdDate).toLocaleString() : '—'}</p>
                      <div className="card p-3 bg-blue-50">
                        <h5 className="font-bold text-blue-900 text-sm mb-2">Baby Examined</h5>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                          {[['Mode of Delivery', rec.modeOfDelivery], ['Gender', rec.gender], ['APGAR', rec.apgarScore], ['Cry', rec.immediateCryDelayedCry], ['Weight (kg)', rec.weightKg], ['Color', rec.color], ['A/F', rec.af], ['Anal Opening', rec.analOpening], ['Back & Spine', rec.backSpine], ['Genitalia', rec.genitalia], ['Eyes/Nose/Ear', rec.eyesNoseEar], ['Cord', rec.cord], ['CNS', rec.cns], ['CVS', rec.cvs], ['Oral Cavity', rec.oralCavity], ['Limbs', rec.upperLowerLimbs]].map(([label, val]) => (
                            <div key={label}><span className="text-gray-400">{label}:</span> <span className="font-medium">{val && val !== 'string' && val !== 0 ? val : '—'}</span></div>
                          ))}
                        </div>
                      </div>
                      <div className="card p-3 bg-pink-50">
                        <h5 className="font-bold text-pink-900 text-sm mb-2">Risk Factors</h5>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                          {[['Gestation', rec.periodOfGestation], ['Leaking', rec.historyOfLeaking], ['Tetanus', rec.tetanusVaccination], ['Diabetes', rec.historyOfDiabetes], ['Hypertension', rec.historyOfHypertension], ['Twins', rec.historyOfTwins], ['IUGR', rec.historyOfIUGR], ['Preterm', rec.historyOfPreterm], ['IUD', rec.historyOfIUD], ['Anomalous Baby', rec.previousAnomalousBaby], ['Mother Blood Grp', rec.motherBloodGroup], ['HbsAg', rec.hBsAg], ['Anti HCV', rec.antiHCV]].map(([label, val]) => (
                            <div key={label}><span className="text-gray-400">{label}:</span> <span className="font-medium">{val && val !== 'string' ? val : '—'}</span></div>
                          ))}
                        </div>
                      </div>
                      {(rec.advice || rec.injections || rec.others || rec.caseOfReferral) && (
                        <div className="card p-3 bg-green-50">
                          <h5 className="font-bold text-green-900 text-sm mb-2">Advice & Treatment</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                            {[['Advice', rec.advice], ['Injections', rec.injections], ['Others', rec.others], ['Referral', rec.caseOfReferral]].map(([label, val]) => val && val !== 'string' ? (
                              <div key={label}><span className="text-gray-400">{label}:</span> <span className="font-medium">{val}</span></div>
                            ) : null)}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

           <div className="flex justify-between items-center mt-4">
              <button onClick={() => printNurseryRecord(viewNurseryModal)} className="btn-primary">🖨 Print</button>
              <button onClick={() => setViewNurseryModal(null)} className="btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}
{allBabyRecords.length === 0 && <div className="text-center py-12 text-gray-500">No baby records yet</div>}    </div>
  );
};

// Lab Reports View for Doctor
// Lab Reports View for Doctor
// REPLACE the existing LabReportsView component in DoctorPanel.jsx with this:

const LabReportsView = () => {
  const [reports,          setReports]          = useState([]);
  const [dropdownPatients, setDropdownPatients] = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState('');
  const [selectedPatient,  setSelectedPatient]  = useState('');
  const [search,           setSearch]           = useState('');

  const API_TOKEN = 'A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2';
  const BASE_URL  = process.env.NODE_ENV === 'production' ? 'https://api.ruqqiasultanaclinic.com' : '';
  const LAB_HEADERS = { accept: '*/*', 'X-API-TOKEN': API_TOKEN };

  const TEST_TYPES = [
    { id: 1,  name: 'Complete Blood Count (CBC)' },
    { id: 2,  name: 'Blood Sugar (Fasting)' },
    { id: 3,  name: 'Blood Sugar (Random)' },
    { id: 4,  name: 'HbA1c' },
    { id: 5,  name: 'Liver Function Test (LFT)' },
    { id: 6,  name: 'Renal Function Test (RFT)' },
    { id: 7,  name: 'Lipid Profile' },
    { id: 8,  name: 'Thyroid Profile' },
    { id: 9,  name: 'Urine Complete' },
    { id: 10, name: 'Urine Culture' },
    { id: 11, name: 'Hepatitis B (HBsAg)' },
    { id: 12, name: 'Hepatitis C (Anti-HCV)' },
    { id: 13, name: 'HIV Screening' },
    { id: 14, name: 'Blood Group & Rh' },
    { id: 15, name: 'Coagulation Profile' },
    { id: 16, name: 'Electrolytes' },
    { id: 17, name: 'Ultrasound Report' },
    { id: 18, name: 'X-Ray Report' },
    { id: 19, name: 'ECG Report' },
    { id: 0,  name: 'Other' },
  ];

  const getTestName = (id) =>
    TEST_TYPES.find(t => t.id === Number(id))?.name ?? `Test #${id}`;

  const base64ToBlob = (base64) => {
    const raw    = base64.includes(',') ? base64.split(',')[1] : base64;
    const clean  = raw.replace(/[\s\r\n]/g, '');
    const padded = clean + '='.repeat((4 - (clean.length % 4)) % 4);
    const CHUNK  = 8192;
    const parts  = [];
    for (let i = 0; i < padded.length; i += CHUNK) {
      const slice  = padded.slice(i, i + CHUNK);
      const binary = atob(slice);
      const bytes  = new Uint8Array(binary.length);
      for (let j = 0; j < binary.length; j++) bytes[j] = binary.charCodeAt(j);
      parts.push(bytes);
    }
    return new Blob(parts, { type: 'application/pdf' });
  };

  const openPDF = (base64) => {
    const url = URL.createObjectURL(base64ToBlob(base64));
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const downloadPDF = (base64, filename) => {
    const url = URL.createObjectURL(base64ToBlob(base64));
    const a   = Object.assign(document.createElement('a'), { href: url, download: filename || 'report.pdf' });
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5_000);
  };

  // Load patients dropdown
  useEffect(() => {
    getPatientDropdown()
      .then(data => setDropdownPatients(Array.isArray(data) ? data : []))
      .catch(() => setDropdownPatients([]));
  }, []);

  // Load all lab reports
  const loadReports = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(
        `${BASE_URL}/api/Patient/GetLabReport?PatientId=0`,
        { headers: LAB_HEADERS }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setReports(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReports(); }, []);

  const getPatient     = (id) => dropdownPatients.find(p => String(p.id) === String(id));
  const getPatientName = (id) => { const p = getPatient(id); return p ? (p.name || `#${id}`) : `#${id}`; };
  const getPatientMR   = (id) => getPatient(id)?.mrNo || '';

  // Per-row view/download — fetches full base64 by patientId on click
  const printExternalLabResult = (ext, patientName) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Lab Result - ${ext.patientName || patientName}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 30px; font-size: 12px; color: #333; }
          .header { text-align: center; border-bottom: 3px solid #1e40af; padding-bottom: 15px; margin-bottom: 20px; }
          .clinic-name { font-size: 20px; font-weight: bold; color: #1e40af; }
          .subtitle { font-size: 12px; color: #555; margin: 2px 0; }
          .title { font-size: 16px; font-weight: bold; color: #1e40af; text-align: center; margin: 15px 0; background: #e3f2fd; padding: 8px; border-radius: 4px; }
          .section { margin-bottom: 16px; border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden; }
          .section-title { background: #dbeafe; color: #1e40af; padding: 7px 12px; font-weight: bold; font-size: 13px; }
          .section-body { padding: 10px 12px; }
          .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
          .grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; }
          .field { margin-bottom: 4px; }
          .label { color: #888; font-size: 10px; display: block; }
          .value { font-size: 12px; font-weight: 500; }
          .signatures { display: flex; justify-content: space-between; margin-top: 50px; }
          .sig-box { text-align: center; }
          .sig-line { border-top: 1px solid #000; width: 130px; margin: 0 auto 4px; }
          @media print { body { padding: 15px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="clinic-name">${DOCTOR_INFO.clinicName}</div>
          <div class="subtitle">${DOCTOR_INFO.address}</div>
          <div class="subtitle">Phone: ${DOCTOR_INFO.phone}</div>
        </div>
        <div class="title">EXTERNAL LAB RESULT</div>
        <div class="section">
          <div class="section-title">Patient Information</div>
          <div class="section-body grid2">
            <div class="field"><span class="label">Patient Name</span><span class="value">${ext.patientName || patientName || '—'}</span></div>
            <div class="field"><span class="label">MR No</span><span class="value">${ext.patientMRNo || '—'}</span></div>
            <div class="field"><span class="label">Age / Sex</span><span class="value">${ext.ageSex || '—'}</span></div>
            <div class="field"><span class="label">Test Date</span><span class="value">${ext.testDate || '—'}</span></div>
          </div>
        </div>
        <div class="section">
          <div class="section-title">Lab Results</div>
          <div class="section-body grid3">
            ${ext.hBsAg ? `<div class="field"><span class="label">HBsAg</span><span class="value">${ext.hBsAg}</span></div>` : ''}
            ${ext.hcv ? `<div class="field"><span class="label">HCV</span><span class="value">${ext.hcv}</span></div>` : ''}
            ${ext.bloodSugarRandom ? `<div class="field"><span class="label">Blood Sugar (Random)</span><span class="value">${ext.bloodSugarRandom}</span></div>` : ''}
            ${ext.bloodGroup ? `<div class="field"><span class="label">Blood Group</span><span class="value">${ext.bloodGroup}</span></div>` : ''}
            ${ext.rhFactor ? `<div class="field"><span class="label">Rh Factor</span><span class="value">${ext.rhFactor}</span></div>` : ''}
            ${ext.alT_SGPT ? `<div class="field"><span class="label">ALT/SGPT</span><span class="value">${ext.alT_SGPT}</span></div>` : ''}
            ${ext.alp ? `<div class="field"><span class="label">ALP</span><span class="value">${ext.alp}</span></div>` : ''}
            ${ext.bilirubinTotal ? `<div class="field"><span class="label">Bilirubin Total</span><span class="value">${ext.bilirubinTotal}</span></div>` : ''}
            ${ext.urea ? `<div class="field"><span class="label">Urea</span><span class="value">${ext.urea}</span></div>` : ''}
            ${ext.creatinine ? `<div class="field"><span class="label">Creatinine</span><span class="value">${ext.creatinine}</span></div>` : ''}
            ${ext.uricAcid ? `<div class="field"><span class="label">Uric Acid</span><span class="value">${ext.uricAcid}</span></div>` : ''}
            ${ext.pT_ProthrombinTime ? `<div class="field"><span class="label">PT (Prothrombin Time)</span><span class="value">${ext.pT_ProthrombinTime}</span></div>` : ''}
            ${ext.aptt ? `<div class="field"><span class="label">APTT</span><span class="value">${ext.aptt}</span></div>` : ''}
            ${ext.hemoglobin ? `<div class="field"><span class="label">Hemoglobin</span><span class="value">${ext.hemoglobin}</span></div>` : ''}
            ${ext.totalWBCCount ? `<div class="field"><span class="label">Total WBC Count</span><span class="value">${ext.totalWBCCount}</span></div>` : ''}
            ${ext.plateletCount ? `<div class="field"><span class="label">Platelet Count</span><span class="value">${ext.plateletCount}</span></div>` : ''}
          </div>
        </div>
        <div class="signatures">
          <div class="sig-box"><div class="sig-line"></div><div>Lab Technician</div></div>
          <div class="sig-box"><div class="sig-line"></div><div>Doctor / ${DOCTOR_INFO.name}</div></div>
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const ReportActions = ({ report }) => {
    const [busy, setBusy] = useState(false);
    const [err,  setErr]  = useState('');
    const [showExtDropdown, setShowExtDropdown] = useState(false);
    const extResults = report.externalLabResults || [];

    const act = async (type) => {
      setBusy(true);
      setErr('');
      try {
        const r = await fetch(
          `${BASE_URL}/api/Patient/GetLabReport?PatientId=${report.patientId}`,
          { headers: LAB_HEADERS }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const list = await r.json();
        const full = Array.isArray(list) ? list.find(x => x.id === report.id) : null;
        if (!full)            throw new Error('Report not found.');
        if (!full.reportFile) throw new Error('No file attached.');
        const filename = `${getTestName(full.testId)}-${getPatientMR(report.patientId) || 'report'}-ID${full.id}.pdf`;
        if (type === 'view')     openPDF(full.reportFile);
        if (type === 'download') downloadPDF(full.reportFile, filename);
      } catch (e) {
        setErr(e.message);
      } finally {
        setBusy(false);
      }
    };

    return (
      <div>
        <div className="flex gap-1 flex-wrap">
          <button onClick={() => act('view')} disabled={busy}
            className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-40 font-medium transition">
            {busy ? '…' : '👁 View'}
          </button>
          <button onClick={() => act('download')} disabled={busy}
            className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 disabled:opacity-40 font-medium transition">
            {busy ? '…' : '⬇ Download'}
          </button>
          {extResults.length > 0 && (
            <button onClick={() => setShowExtDropdown(!showExtDropdown)}
              title="Show External Lab Results"
              className={`text-xs px-2 py-1 rounded font-bold transition border ${showExtDropdown ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-indigo-600 border-indigo-300 hover:bg-indigo-50'}`}>
              {showExtDropdown ? '▲' : '▼'} {extResults.length}
            </button>
          )}
        </div>
        {showExtDropdown && extResults.length > 0 && (
          <div className="mt-2 border border-indigo-200 rounded-lg overflow-hidden bg-white shadow-sm">
            <div className="bg-indigo-50 px-3 py-1.5 border-b border-indigo-200">
              <p className="text-xs font-bold text-indigo-700">🧪 External Lab Results ({extResults.length})</p>
            </div>
            {extResults.map((ext, idx) => (
              <div key={ext.id || idx} className={`px-3 py-2 ${idx < extResults.length - 1 ? 'border-b border-gray-100' : ''}`}>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs mb-1.5">
                  {ext.patientName && <span><span className="text-gray-400">Patient:</span> <span className="font-medium">{ext.patientName}</span></span>}
                  {ext.testDate    && <span><span className="text-gray-400">Date:</span> <span className="font-medium">{ext.testDate}</span></span>}
                  {ext.ageSex      && <span><span className="text-gray-400">Age/Sex:</span> <span className="font-medium">{ext.ageSex}</span></span>}
                  {ext.patientMRNo && <span><span className="text-gray-400">MR No:</span> <span className="font-medium">{ext.patientMRNo}</span></span>}
                  {ext.hBsAg           && <span><span className="text-gray-400">HBsAg:</span> <span className="font-medium">{ext.hBsAg}</span></span>}
                  {ext.hcv             && <span><span className="text-gray-400">HCV:</span> <span className="font-medium">{ext.hcv}</span></span>}
                  {ext.bloodSugarRandom && <span><span className="text-gray-400">BSR:</span> <span className="font-medium">{ext.bloodSugarRandom}</span></span>}
                  {ext.bloodGroup      && <span><span className="text-gray-400">Blood Group:</span> <span className="font-medium">{ext.bloodGroup}</span></span>}
                  {ext.hemoglobin      && <span><span className="text-gray-400">Hb:</span> <span className="font-medium">{ext.hemoglobin}</span></span>}
                  {ext.plateletCount   && <span><span className="text-gray-400">Platelets:</span> <span className="font-medium">{ext.plateletCount}</span></span>}
                  {ext.urea            && <span><span className="text-gray-400">Urea:</span> <span className="font-medium">{ext.urea}</span></span>}
                  {ext.creatinine      && <span><span className="text-gray-400">Creatinine:</span> <span className="font-medium">{ext.creatinine}</span></span>}
                  {ext.alT_SGPT        && <span><span className="text-gray-400">ALT:</span> <span className="font-medium">{ext.alT_SGPT}</span></span>}
                  {ext.uricAcid        && <span><span className="text-gray-400">Uric Acid:</span> <span className="font-medium">{ext.uricAcid}</span></span>}
                </div>
                <button onClick={() => printExternalLabResult(ext, getPatientName(report.patientId))}
                  className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 font-medium">
                  🖨 Print
                </button>
              </div>
            ))}
          </div>
        )}
        {err && <p className="text-xs text-red-500 mt-1">{err}</p>}
      </div>
    );
  };
  const filteredReports = reports
    .filter(r => !selectedPatient || String(r.patientId) === String(selectedPatient))
    .filter(r => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        getPatientName(r.patientId).toLowerCase().includes(q) ||
        getPatientMR(r.patientId).toLowerCase().includes(q)   ||
        getTestName(r.testId).toLowerCase().includes(q)
      );
    });

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <h2 className="text-xl font-bold text-gray-900">🔬 Lab Reports</h2>
        <div className="flex items-center gap-2">
          {loading && <span className="text-xs text-blue-500 animate-pulse">🔄 Loading...</span>}
          {!loading && !error && (
            <span className="text-xs text-green-600">✅ {reports.length} reports</span>
          )}
          <button onClick={loadReports} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">🔄 Refresh</button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">⚠️ {error}</div>
      )}

      {/* Filters */}
      <div className="card p-4 flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium mb-1">Filter by Patient</label>
          <select value={selectedPatient} onChange={e => setSelectedPatient(e.target.value)} className="input-field w-full">
            <option value="">All Patients</option>
            {dropdownPatients.map(p => (
              <option key={p.id} value={p.id}>{p.name}{p.mrNo ? ` (${p.mrNo})` : ''}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium mb-1">Search</label>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Patient, MR No, test name..." className="input-field w-full" />
        </div>
        {(selectedPatient || search) && (
          <button onClick={() => { setSelectedPatient(''); setSearch(''); }} className="btn-secondary text-sm">✕ Clear</button>
        )}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Patient</th>
                <th>MR No</th>
                <th>Test</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-8 text-gray-400">⏳ Loading...</td></tr>
              ) : filteredReports.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-8 text-gray-500">
                  {reports.length === 0 ? 'No lab reports found' : 'No reports match your filter'}
                </td></tr>
              ) : filteredReports.map(r => (
                <tr key={r.id}>
                  <td className="font-mono text-xs text-gray-500">#{r.id}</td>
                  <td className="font-medium">{getPatientName(r.patientId)}</td>
<td className="font-mono text-xs text-gray-500">{r.mrNo || getPatientMR(r.patientId) || '—'}</td>
                  <td>{getTestName(r.testId)}</td>
                  <td className="text-gray-500 max-w-[160px] truncate" title={r.notes}>{r.notes || '—'}</td>
                  <td><ReportActions report={r} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};
// Outdoor Patient Prescription Component
// Outdoor Patient Prescription Component
const OutdoorPatientPrescription = () => {
  const { outdoorPrescriptions, addOutdoorPrescription, deleteOutdoorPrescription } = useData();
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
 const [submitting, setSubmitting] = useState(false);
  const [apiPrescriptions, setApiPrescriptions] = useState([]);
  const [viewPrescription, setViewPrescription] = useState(null);
  const [loadingList, setLoadingList] = useState(true);

  const API_TOKEN = 'A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2';
  const BASE_URL = process.env.NODE_ENV === 'production' ? 'https://api.ruqqiasultanaclinic.com' : '';

  const loadPrescriptions = async () => {
    setLoadingList(true);
    try {
      const res = await fetch(`${BASE_URL}/api/Patient/GetOutdoorPatient`, {
        headers: { 'accept': '*/*', 'X-API-TOKEN': API_TOKEN },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setApiPrescriptions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load prescriptions:', err.message);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { loadPrescriptions(); }, []);

  const [form, setForm] = useState({
    patientName: '',
    age: '',
    phone: '',
    address: '',
    complaints: '',
    diagnosis: '',
    medicines: [{ name: '', dosage: '', duration: '', instructions: '' }],
    tests: '',
    advice: '',
    followUp: '',
    doctorName: DOCTOR_INFO.name
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const API_TOKEN = 'A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2A9F3C8D1E7B4F6A2C9E1D8B7F4A6C5E2';
    const BASE_URL = process.env.NODE_ENV === 'production' ? 'https://api.ruqqiasultanaclinic.com' : '';

    const payload = {
      patientName: form.patientName,
      age: Number(form.age) || 0,
      phone: form.phone || '',
      address: form.address || '',
      chiefComplaints: form.complaints || '',
      diagnosis: form.diagnosis || '',
      recommendedTests: form.tests || '',
      advice: form.advice || '',
      followUp: form.followUp || '',
      medicines: form.medicines.map(m => ({
        medicineName: m.name,
        dosage: m.dosage,
        duration: m.duration,
        instructions: m.instructions,
      })),
    };

    try {
      const res = await fetch(`${BASE_URL}/api/Patient/CreateOutdoorPatient`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': '*/*',
          'X-API-TOKEN': API_TOKEN,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      console.log('Outdoor patient saved to API:', result);
      await loadPrescriptions();
      alert('Prescription saved successfully!');
      resetForm();
    } catch (err) {
      alert('Failed to save prescription: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({
      patientName: '',
      age: '',
      phone: '',
      address: '',
      complaints: '',
      diagnosis: '',
      medicines: [{ name: '', dosage: '', duration: '', instructions: '' }],
      tests: '',
      advice: '',
      followUp: '',
      doctorName: DOCTOR_INFO.name
    });
    setShowForm(false);
  };

  const addMedicineRow = () => {
    setForm({
      ...form,
      medicines: [...form.medicines, { name: '', dosage: '', duration: '', instructions: '' }]
    });
  };

  const removeMedicineRow = (index) => {
    setForm({
      ...form,
      medicines: form.medicines.filter((_, i) => i !== index)
    });
  };

  const updateMedicine = (index, field, value) => {
    const newMedicines = [...form.medicines];
    newMedicines[index][field] = value;
    setForm({ ...form, medicines: newMedicines });
  };

  const printPrescription = (prescription) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Prescription - ${prescription.prescriptionNo}</title>
        <style>
          @page { margin: 0.5in; }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            color: #333;
          }
          .header {
            text-align: center;
            border-bottom: 3px solid #2563eb;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .header h1 {
            margin: 0;
            color: #1e40af;
            font-size: 24px;
          }
          .header .subtitle {
            color: #6b7280;
            margin: 5px 0;
            font-size: 14px;
          }
          .header .contact {
            font-size: 12px;
            color: #6b7280;
            margin-top: 8px;
          }
          .prescription-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
            padding: 10px;
            background: #f3f4f6;
            border-radius: 5px;
          }
          .section {
            margin-bottom: 20px;
          }
          .section-title {
            font-weight: bold;
            color: #1e40af;
            border-bottom: 2px solid #e5e7eb;
            padding-bottom: 5px;
            margin-bottom: 10px;
            font-size: 16px;
          }
          .patient-info {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            margin-bottom: 20px;
          }
          .patient-info p {
            margin: 5px 0;
            font-size: 14px;
          }
          .medicines-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          .medicines-table th,
          .medicines-table td {
            border: 1px solid #e5e7eb;
            padding: 8px;
            text-align: left;
            font-size: 13px;
          }
          .medicines-table th {
            background: #f3f4f6;
            font-weight: bold;
            color: #1e40af;
          }
          .rx-symbol {
            font-size: 24px;
            color: #2563eb;
            font-weight: bold;
            margin-bottom: 10px;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e5e7eb;
            text-align: right;
          }
          .signature-line {
            margin-top: 50px;
            text-align: right;
          }
          @media print {
            body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${DOCTOR_INFO.nameUrdu}</h1>
          <h1>${DOCTOR_INFO.name}</h1>
          <p class="subtitle">${DOCTOR_INFO.title}</p>
          <p class="subtitle">${DOCTOR_INFO.degrees.join(', ')}</p>
          <p class="subtitle">PMDC: ${DOCTOR_INFO.pmdc} | ${DOCTOR_INFO.college}</p>
          <p class="contact">${DOCTOR_INFO.address}</p>
          <p class="contact">Phone: ${DOCTOR_INFO.phone} | OPD: ${DOCTOR_INFO.opdTiming}</p>
        </div>

        <div class="prescription-info">
          <div>
            <strong>Prescription No:</strong> ${prescription.prescriptionNo}<br>
            <strong>Date:</strong> ${prescription.date}
          </div>
          <div style="text-align: right;">
            <strong>Time:</strong> ${prescription.time}
          </div>
        </div>

        <div class="patient-info">
          <div>
            <p><strong>Patient Name:</strong> ${prescription.patientName}</p>
            <p><strong>Age:</strong> ${prescription.age} years</p>
          </div>
          <div>
            <p><strong>Phone:</strong> ${prescription.phone || 'N/A'}</p>
            <p><strong>Address:</strong> ${prescription.address || 'N/A'}</p>
          </div>
        </div>

        ${prescription.complaints ? `
        <div class="section">
          <div class="section-title">Chief Complaints</div>
          <p>${prescription.complaints}</p>
        </div>
        ` : ''}

        ${prescription.diagnosis ? `
        <div class="section">
          <div class="section-title">Diagnosis</div>
          <p>${prescription.diagnosis}</p>
        </div>
        ` : ''}

        <div class="section">
          <div class="section-title"><span class="rx-symbol">Rx</span> Medicines</div>
          <table class="medicines-table">
            <thead>
              <tr>
                <th style="width: 5%">#</th>
                <th style="width: 35%">Medicine Name</th>
                <th style="width: 20%">Dosage</th>
                <th style="width: 15%">Duration</th>
                <th style="width: 25%">Instructions</th>
              </tr>
            </thead>
            <tbody>
              ${prescription.medicines.map((med, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td><strong>${med.name}</strong></td>
                  <td>${med.dosage}</td>
                  <td>${med.duration}</td>
                  <td>${med.instructions}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        ${prescription.tests ? `
        <div class="section">
          <div class="section-title">Recommended Tests</div>
          <p>${prescription.tests}</p>
        </div>
        ` : ''}

        ${prescription.advice ? `
        <div class="section">
          <div class="section-title">Advice</div>
          <p>${prescription.advice}</p>
        </div>
        ` : ''}

        ${prescription.followUp ? `
        <div class="section">
          <div class="section-title">Follow-up</div>
          <p>${prescription.followUp}</p>
        </div>
        ` : ''}

        <div class="signature-line">
          <p style="margin-bottom: 5px;"><strong>${prescription.doctorName}</strong></p>
          <p style="margin: 0; color: #6b7280; font-size: 12px;">Signature</p>
        </div>

        <div class="footer">
          <p style="font-size: 11px; color: #6b7280; margin: 0;">This is a computer-generated prescription</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

const filteredPrescriptions = apiPrescriptions.filter(p =>
    p.patientName?.toLowerCase().includes(search.toLowerCase()) ||
    p.phone?.includes(search) ||
    p.diagnosis?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <h2 className="text-xl font-bold text-gray-900">🩺 Outdoor Patient Prescriptions</h2>
        <div className="flex items-center gap-2">
          {loadingList && <span className="text-xs text-blue-500 animate-pulse">🔄 Loading...</span>}
          {!loadingList && <span className="text-xs text-green-600">✅ {apiPrescriptions.length} records</span>}
          <button onClick={loadPrescriptions} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">🔄</button>
          <button onClick={() => setShowForm(true)} className="btn-primary text-sm">+ New Prescription</button>
        </div>
      </div>

      {/* Prescription Form */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content p-6 max-w-5xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">New Prescription</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-500 text-2xl">×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Patient Information */}
              <div className="card p-4 bg-blue-50">
                <h4 className="font-bold text-gray-900 mb-3">Patient Information</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Patient Name *</label>
                    <input type="text" value={form.patientName} onChange={(e) => setForm({ ...form, patientName: e.target.value })} className="input-field" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Age *</label>
                    <input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="input-field" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone</label>
                    <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-field" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Address</label>
                    <input type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input-field" />
                  </div>
                </div>
              </div>

              {/* Clinical Information */}
              <div className="card p-4">
                <h4 className="font-bold text-gray-900 mb-3">Clinical Information</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Chief Complaints</label>
                    <textarea value={form.complaints} onChange={(e) => setForm({ ...form, complaints: e.target.value })} className="input-field" rows="2" placeholder="Patient's main complaints"></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Diagnosis</label>
                    <textarea value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} className="input-field" rows="2" placeholder="Clinical diagnosis"></textarea>
                  </div>
                </div>
              </div>

              {/* Medicines */}
              <div className="card p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-bold text-gray-900">Rx Medicines *</h4>
                  <button type="button" onClick={addMedicineRow} className="btn-success text-xs">+ Add Medicine</button>
                </div>
                <div className="space-y-3">
                  {form.medicines.map((med, index) => (
                    <div key={index} className="grid md:grid-cols-5 gap-2 items-end p-3 bg-gray-50 rounded">
                      <div>
                        <label className="block text-xs font-medium mb-1">Medicine Name *</label>
                        <input type="text" value={med.name} onChange={(e) => updateMedicine(index, 'name', e.target.value)} className="input-field text-sm" required />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Dosage *</label>
                        <input type="text" value={med.dosage} onChange={(e) => updateMedicine(index, 'dosage', e.target.value)} className="input-field text-sm" placeholder="e.g., 1 tablet" required />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Duration *</label>
                        <input type="text" value={med.duration} onChange={(e) => updateMedicine(index, 'duration', e.target.value)} className="input-field text-sm" placeholder="e.g., 7 days" required />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Instructions *</label>
                        <input type="text" value={med.instructions} onChange={(e) => updateMedicine(index, 'instructions', e.target.value)} className="input-field text-sm" placeholder="e.g., After meals" required />
                      </div>
                      <div>
                        {form.medicines.length > 1 && (
                          <button type="button" onClick={() => removeMedicineRow(index)} className="btn-danger text-xs w-full">Remove</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Information */}
              <div className="card p-4">
                <h4 className="font-bold text-gray-900 mb-3">Additional Information</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Recommended Tests</label>
                    <textarea value={form.tests} onChange={(e) => setForm({ ...form, tests: e.target.value })} className="input-field" rows="2" placeholder="List any recommended lab tests or investigations"></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Advice</label>
                    <textarea value={form.advice} onChange={(e) => setForm({ ...form, advice: e.target.value })} className="input-field" rows="2" placeholder="General advice for the patient"></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Follow-up</label>
                    <input type="text" value={form.followUp} onChange={(e) => setForm({ ...form, followUp: e.target.value })} className="input-field" placeholder="e.g., Follow-up after 1 week" />
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <button type="submit" disabled={submitting} className="btn-primary flex items-center gap-2 disabled:opacity-60">
                  {submitting ? (
                    <><span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span> Saving...</>
                  ) : 'Save & Print Prescription'}
                </button>
                <button type="button" onClick={resetForm} className="btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="card p-4">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 Search by patient name, prescription number, or phone..." className="input-field" />
      </div>

      {/* Prescriptions List */}
      <div className="card p-6">
        <h3 className="font-bold text-gray-900 mb-4">Prescription History</h3>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>#ID</th>
<th>Patient Name</th>
<th>Age</th>
<th>Phone</th>
<th>Diagnosis</th>
<th>Actions</th>
              </tr>
            </thead>
            <tbody>
{loadingList ? (
                <tr><td colSpan="6" className="text-center py-8 text-gray-400">
                  <span className="animate-spin inline-block w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full mr-2"></span>Loading...
                </td></tr>
              ) : filteredPrescriptions.slice().reverse().map(prescription => (
                <tr key={prescription.id}>
                  <td className="font-medium text-blue-600">#{prescription.id}</td>
                  <td className="font-medium">{prescription.patientName}</td>
                  <td>{prescription.age}</td>
                  <td>{prescription.phone || '-'}</td>
                  <td>{prescription.diagnosis || '-'}</td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => printPrescription({
                        ...prescription,
                        prescriptionNo: `RX-${prescription.id}`,
                        date: new Date().toLocaleDateString(),
                        time: new Date().toLocaleTimeString(),
                        complaints: prescription.chiefComplaints,
                        tests: prescription.recommendedTests,
                        medicines: (prescription.medicines || []).map(m => ({
                          name: m.medicineName,
                          dosage: m.dosage,
                          duration: m.duration,
                          instructions: m.instructions,
                        })),
                        doctorName: DOCTOR_INFO.name,
                      })} className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200">🖨 Print</button>
                      <button onClick={() => setViewPrescription(prescription)} className="text-xs px-2 py-1 bg-indigo-100 text-indigo-600 rounded hover:bg-indigo-200">👁 View</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredPrescriptions.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No outdoor patient prescriptions found
            </div>
          )}
        </div>
      </div>
      {/* View Prescription Modal */}
      {viewPrescription && (
        <div className="modal-overlay" onClick={() => setViewPrescription(null)}>
          <div className="modal-content p-6 max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Prescription #{viewPrescription.id}</h3>
              <button onClick={() => setViewPrescription(null)} className="text-gray-500 text-2xl">×</button>
            </div>

            <div className="space-y-4">
              <div className="card p-4 bg-blue-50">
                <h4 className="font-bold text-blue-900 mb-2">Patient Information</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p><span className="text-gray-500">Name:</span> <strong>{viewPrescription.patientName}</strong></p>
                  <p><span className="text-gray-500">Age:</span> {viewPrescription.age}</p>
                  <p><span className="text-gray-500">Phone:</span> {viewPrescription.phone || '—'}</p>
                  <p><span className="text-gray-500">Address:</span> {viewPrescription.address || '—'}</p>
                </div>
              </div>

              {viewPrescription.chiefComplaints && (
                <div className="card p-4">
                  <h4 className="font-bold text-gray-700 mb-1">Chief Complaints</h4>
                  <p className="text-sm">{viewPrescription.chiefComplaints}</p>
                </div>
              )}

              {viewPrescription.diagnosis && (
                <div className="card p-4">
                  <h4 className="font-bold text-gray-700 mb-1">Diagnosis</h4>
                  <p className="text-sm">{viewPrescription.diagnosis}</p>
                </div>
              )}

              <div className="card p-4">
                <h4 className="font-bold text-gray-700 mb-3">💊 Medicines</h4>
                <table className="data-table text-sm">
                  <thead><tr><th>#</th><th>Medicine</th><th>Dosage</th><th>Duration</th><th>Instructions</th></tr></thead>
                  <tbody>
                    {(viewPrescription.medicines || []).map((m, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td className="font-medium">{m.medicineName}</td>
                        <td>{m.dosage}</td>
                        <td>{m.duration}</td>
                        <td>{m.instructions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {viewPrescription.recommendedTests && (
                <div className="card p-4">
                  <h4 className="font-bold text-gray-700 mb-1">Recommended Tests</h4>
                  <p className="text-sm">{viewPrescription.recommendedTests}</p>
                </div>
              )}

              {viewPrescription.advice && (
                <div className="card p-4">
                  <h4 className="font-bold text-gray-700 mb-1">Advice</h4>
                  <p className="text-sm">{viewPrescription.advice}</p>
                </div>
              )}

              {viewPrescription.followUp && (
                <div className="card p-4">
                  <h4 className="font-bold text-gray-700 mb-1">Follow-up</h4>
                  <p className="text-sm">{viewPrescription.followUp}</p>
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-4">
              <button onClick={() => printPrescription({
                ...viewPrescription,
                prescriptionNo: `RX-${viewPrescription.id}`,
                date: new Date().toLocaleDateString(),
                time: new Date().toLocaleTimeString(),
                complaints: viewPrescription.chiefComplaints,
                tests: viewPrescription.recommendedTests,
                medicines: (viewPrescription.medicines || []).map(m => ({
                  name: m.medicineName,
                  dosage: m.dosage,
                  duration: m.duration,
                  instructions: m.instructions,
                })),
                doctorName: DOCTOR_INFO.name,
              })} className="btn-primary flex-1">🖨 Print Prescription</button>
              <button onClick={() => setViewPrescription(null)} className="btn-secondary flex-1">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorPanel;
  